import { Component, OnDestroy, OnInit, inject, NO_ERRORS_SCHEMA, ChangeDetectorRef, Injector, HostListener, AfterViewChecked, ViewChild, ElementRef } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Storage, ref, uploadBytesResumable, getDownloadURL, UploadTask, uploadBytes, deleteObject } from '@angular/fire/storage';
import { Auth } from '@angular/fire/auth';;
import { Firestore, getDoc, doc, collection, addDoc } from '@angular/fire/firestore';
import { Observable, Subscription, firstValueFrom, map, combineLatest } from 'rxjs';
import { ExcelUploaderComponent } from '../excel-uploader/excel-uploader.component';
import { UploadHistoryComponent } from '../excel-uploader/upload-history.component';
import { ExcelImportService } from '../services/excel-import.service';
import * as XLSX from 'xlsx';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { EmailFormComponent } from '../email-form/email-form.component';
import { ExchangeRateService } from '../services/exchange-rate.service';
import { Invoice } from '../models/invoice.model';
import { SmrsRatePopupComponent, KeyGroup, SmrsRate } from '../smrs-rate-popup/smrs-rate-popup.component';
import { FeeRateService } from '../services/fee-rate.service';
import { InvoiceService, GenerationPlan, GenerationCompanyPlan, GenerationResult, StaleGenerationPlanError } from '../services/invoice.service';
import { willGenerate } from '../services/smrs-generation.util';
import { AccessService, PagePermission, AppUser } from '../services/access.service';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { PhotoboothService } from '../services/photobooth.service';
import { StoreService } from '../services/store.service';
import { SalesReportModalComponent } from '../sales-report/sales-report-modal.component';

@Component({
  selector: 'app-invoice',
  standalone: true,
  imports: [CommonModule, FormsModule, ExcelUploaderComponent, UploadHistoryComponent, EmailFormComponent, HttpClientModule, SmrsRatePopupComponent, SalesReportModalComponent],
  providers: [ExchangeRateService],
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.css']
})
export class InvoiceComponent implements OnInit, OnDestroy, AfterViewChecked {
  private storage = inject(Storage);
  @ViewChild(SmrsRatePopupComponent) rateCustomization!: SmrsRatePopupComponent;
  @ViewChild('manualInvoiceBody') manualInvoiceBody!: ElementRef;
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private excelService = inject(ExcelImportService);
  private pdfService = inject(PdfGeneratorService);
  private exchangeRateService = inject(ExchangeRateService);
  private feeRateService = inject(FeeRateService);
  private invoiceService = inject(InvoiceService);
  private accessService = inject(AccessService);
  private brandService = inject(BrandService);
  private photoboothService = inject(PhotoboothService);
  private storeService = inject(StoreService);
  selectedBrand: Brand | null = null;
  private injector = inject(Injector);
  private cd = inject(ChangeDetectorRef);
  rates: SmrsRate[] = [];
  currentViewRates: SmrsRate[] = []; // For Invoice Modal / PDF
  manualRates: SmrsRate[] = []; // For Manual Invoice Modal
  extractedKeys: KeyGroup[] = [];
  periods: string[] = [];
  private subscriptions: Subscription[] = [];
  private currentUploadTask: UploadTask | null = null;
  isImporting: boolean = false;
  isGeneratingInvoices: boolean = false;
  importProgress: number = 0;
  importStatusText: string = 'Preparing import...';
  selectedPeriod: string | undefined;
  isFileProcessed: boolean = false; // This is for period processing, not file upload
  showUploader: boolean = true;

  companies: any[] = [];
  stores: any[] = [];
  selectedRowIds: Set<string> = new Set();

  isInvoiceModalOpen = false;
  selectedCompany: any = null;
  currentDate = new Date();
  isLoadingInvoice = false;
  importSuccessMessage: string = '';
  isSavingPDFs = false;
  expandedRowId: string | null = null;
  expandedCompanyTotals: any = null;
  gridData: any[] = [];
  filteredGridData: any[] = [];
  filterStatus: string = '';
  filterEmailStatus: string = '';
  filterCompany: string = '';
  filterCountry: string = 'All';
  availableCountries: string[] = [];
  private companyCountryMap = new Map<string, string>();
  private companyCurrencyMap = new Map<string, string>();
  private exchangeRateCache = new Map<string, { rate: number, date?: Date }>();
  isFilterRowVisible: boolean = false;
  isLoadingGrid = false;
  isLoading = true;
  private isInitialized = false;
  newlyAddedStores: string[] = [];
  isNewStoresModalOpen: boolean = false;
  skippedRows: any[] = [];
  isEmailModalOpen: boolean = false;
  emailData: any = { to: '', subject: '', body: '', attachments: [] };
  activeTab: 'import' | 'management' = 'management';
  currentEmailRow: any = null;
  editingValues: any = {};
  isRecipientsModalOpen: boolean = false;
  currentRecipients: string[] = [];
  savedScrollPosition = 0;
  shouldRestoreScroll = false;
  lastLoadedPeriod: string | undefined;
  cachedAggregations: any[] = [];
  private gridDataCache = new Map<string, any[]>();
  private fileProcessedCache = new Map<string, boolean>();
  private rawDataCache = new Map<string, any[]>();
  filterCompanyIds: Set<string> = new Set();
  showCompanyFilterDropdown = false;
  showCountryFilterDropdown = false;
  userProfile: AppUser | null = null;
  pagePermissions: PagePermission[] = [];

  get isSuperAdmin(): boolean {
    return (this.userProfile?.role || '').toLowerCase() === 'superadmin';
  }

  get canDeleteInvoices(): boolean {
    const role = (this.userProfile?.role || '').toLowerCase();
    if (!role) return false;
    if (role === 'superadmin') return true;
    const perm = this.pagePermissions.find(p => p.id === 'invoice_delete-invoices');
    return perm ? perm.roles.map(r => r.toLowerCase()).includes(role) : false;
  }

  unlockedInvoiceRowIds: Set<string> = new Set<string>();

  async toggleUnlockInvoiceRow(row: any): Promise<void> {
    if (!this.isSuperAdmin || !row) return;
    const isCurrentlyUnlocked = this.unlockedInvoiceRowIds.has(row.id) || row.isManuallyUnlocked;
    const shouldUnlock = !isCurrentlyUnlocked;

    if (shouldUnlock) {
      this.unlockedInvoiceRowIds.add(row.id);
    } else {
      this.unlockedInvoiceRowIds.delete(row.id);
    }

    row.isManuallyUnlocked = shouldUnlock;
    row.isLocked = !shouldUnlock;
    this.cd.detectChanges();

    const userName = this.auth.currentUser?.displayName || this.auth.currentUser?.email || 'Superadmin';
    const companyId = row.company?.id || row.companyId || (typeof row.company === 'string' ? row.company : '');
    const companyName = row.company?.company || row.companyName || '';
    try {
      const savedDocId = await this.invoiceService.toggleUnlockInvoice(companyId, row.period, shouldUnlock, userName, row.invoiceDocId, companyName);
      if (savedDocId && !row.invoiceDocId) {
        row.invoiceDocId = savedDocId;
      }
    } catch (e) {
      console.error('Failed to persist unlock state to Firestore', e);
    }

    this.gridDataCache.clear();
    this.rawDataCache.clear();
    await this.recalculateLocks();
    this.cd.detectChanges();
  }

  // Incentive Stats
  incentiveStats = {
    rateNames: [] as string[],
    lfc: {} as { [key: string]: number },
    kceg: {} as { [key: string]: number },
    kcegRates: {} as { [key: string]: number },
    lfcRates: {} as { [key: string]: number },
    total: {} as { [key: string]: number },
    grandTotalLfc: 0,
    grandTotalKceg: 0,
    grandTotal: 0
  };

  // Dashboard Stats
  dashboardStats = {
    totalRevenue: 0,
    totalFees: 0,
    totalDue: 0,
    totalCollected: 0,
    totalOutstanding: 0,
    collectionRate: 0,
    statusCounts: { Paid: 0, Partial: 0, 'Not Paid': 0, Canceled: 0 } as Record<string, number>
  };

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  pageSizeOptions: number[] = [10, 50, 100];

  // Manual Invoice Modal
  isManualModalOpen: boolean = false;
  isManualEditMode: boolean = false;
  manualNewStoreId: string = '';
  manualBeginningBalance: number = 0;
  isLoadingManualBalance: boolean = false;
  existingCompanyIdsForManualPeriod: Set<string> = new Set();
  manualInvoiceData: {
    companyId: string;
    period: string;
    items: {
      storeId: string; revenues: { [key: string]: number };
      isCustom?: boolean;
    }[];
    adjustments: { name: string; amounts: { [key: string]: number } }[];
  } = { companyId: '', period: '', items: [], adjustments: [] };

  // Sales Report Modal State (from company name link)
  isSalesReportModalOpen: boolean = false;
  salesReportCompany: any = null;
  salesReportPeriod: string = '';
  salesReportTotalRevenue: number = 0;

  openSalesReportModal(row: any) {
    if (!row || !row.company) return;
    this.salesReportCompany = row.company;
    this.salesReportPeriod = row.period || this.selectedPeriod || '';
    this.salesReportTotalRevenue = row.totalRevenueUSD || 0;
    this.isSalesReportModalOpen = true;
  }

  closeSalesReportModal() {
    this.isSalesReportModalOpen = false;
    this.salesReportCompany = null;
    this.salesReportPeriod = '';
    this.salesReportTotalRevenue = 0;
  }

  // Duplicate Warning
  duplicateWarningData: { file: File, rows: any[], existingRecord: any, storeMap?: Map<string, string> } | null = null;

  // Historical Import Confirmation Modal (Superadmin Only)
  historicalImportConfirmationData: {
    file: File;
    rawRows: any[];
    storeMap: Map<string, string>;
    period: string;
    brandName: string;
    detectedCompanies: string[];
    affectedFuturePeriods: string[];
    affectedCompanyCount: number;
  } | null = null;

  // Generate SMRS Invoices review dialog (KAN-284)
  isGenerationPreviewOpen = false;
  isPlanningGeneration = false;
  generationPlan: GenerationPlan | null = null;
  generationPlanError = '';
  isGenerationStale = false;
  generationResult: GenerationResult | null = null;
  expandedGenerationRows: Set<string> = new Set();

  // History Modal
  isHistoryModalOpen = false;
  invoiceHistory: any[] = [];
  isLoadingHistory = false;

  // Invoice Data
  invoiceItems: any[] = [];
  invoiceTotals = {
    revenues: {} as { [key: string]: number },
    fees: {} as { [key: string]: number },
    grandTotalRevenue: 0,
    totalFee: 0,
    beginningBalance: 0,
    totalDue: 0,
    isInternational: false,
    country: 'USA',
    currency: 'USD',
    exchangeRate: 1,
    exchangeRateDate: undefined as Date | undefined,
    totalDueUSD: 0
  };
  invoiceNumber: string = '';

  constructor() {
  }

  ngOnInit() {
    this.subscriptions.push(
      combineLatest([
        this.feeRateService.getRates(),
        this.invoiceService.getCompanies(),
        this.invoiceService.getStores(),
        this.brandService.selectedBrand$
      ]).subscribe(async ([rates, companies, stores, selectedBrand]) => {
        const ratesChanged = JSON.stringify(rates) !== JSON.stringify(this.rates);
        const brandChanged = this.selectedBrand?.id !== selectedBrand?.id;
        this.rates = rates;
        this.selectedBrand = selectedBrand;

        // Apply Page Scope / Owner restrictions & Action Permissions
        const user = this.auth.currentUser;
        let finalCompanies = companies;
        let finalStores = stores;

        if (user) {
          const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
          this.userProfile = profile || null;
          const scope = await this.accessService.getUserPageScope('invoice', profile);
          if (scope.isRestricted) {
            const allowedCompanySet = new Set(scope.companyIds);
            const allowedStoreSet = new Set(scope.storeIds.map(s => s.toLowerCase()));
            finalCompanies = companies.filter(c => allowedCompanySet.has(c.id));
            finalStores = stores.filter(s => {
              const sid = (s.storeId || s.id || '').toString().toLowerCase();
              return allowedStoreSet.has(sid);
            });
          }
        }

        const perms = await firstValueFrom(this.accessService.getPagePermissions());
        this.pagePermissions = perms || [];

        this.companies = finalCompanies.sort((a: any, b: any) => (a.company || '').localeCompare(b.company || ''));
        this.stores = finalStores;
        this.gridDataCache.clear();
        this.rawDataCache.clear();
        this.fileProcessedCache.clear();

        // Initialize filters
        this.selectAllCompanies();
        await this.initCompanyFilters();

        if (brandChanged) {
          if (this.activeBrandName !== 'LFC' && this.activeTab === 'import') {
            this.activeTab = 'management';
          }
          this.selectedRowIds.clear();
        }

        if (this.isManualModalOpen && this.manualInvoiceData.period) {
          this.manualRates = this.getRatesForPeriod(this.manualInvoiceData.period);
        }

        if (!this.isInitialized) {
          this.isInitialized = true;
          // Do not load any period by default. Wait for user selection.
          this.isLoading = false;
          if (this.selectedPeriod) {
            this.loadGridData();
          }
        } else {
          if (ratesChanged && this.selectedPeriod && this.selectedPeriod !== 'All Periods') {
            this.isLoadingGrid = true;
            this.invoiceService.recalculateInvoicesForPeriod(this.selectedPeriod, rates, stores)
              .then(() => this.loadGridData())
              .catch(err => {
                console.error('Error recalculating invoices:', err);
                this.loadGridData();
              });
          } else {
            this.loadGridData();
          }
        }
      })
    );

    this.generatePeriods();

    this.subscriptions.push(
      this.excelService.isImporting$.subscribe(isImporting => {
        this.isImporting = isImporting;
      }),
      this.excelService.importProgress$.subscribe(progress => {
        this.importProgress = progress;
      }),
      this.excelService.currentImportPeriod$.subscribe(period => {
        if (period) {
          this.selectedPeriod = period;
          this.onPeriodChange(period);
        }
      })
    );
  }

  generatePeriods() {
    this.periods = [];
    const startDate = new Date(2023, 0, 1); // Jan 2023
    const currentDate = new Date();
    const tempDate = new Date(startDate);

    while (tempDate <= currentDate) {
      const year = tempDate.getFullYear();
      const month = (tempDate.getMonth() + 1).toString().padStart(2, '0');
      this.periods.push(`${year}/${month}`);
      tempDate.setMonth(tempDate.getMonth() + 1);
    }
    this.periods.reverse(); // Show newest dates first
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngAfterViewChecked() {
    if (this.shouldRestoreScroll && !this.isLoadingGrid) {
      window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' });
      this.shouldRestoreScroll = false;
    }
  }

  get activeBrandTab(): string {
    return this.selectedBrand?.id || '';
  }

  get activeBrandName(): string {
    return this.selectedBrand?.name || '';
  }

  get activeTabCompanies() {
    return this.companies.filter(c => {
      const b = (c.brand || '').trim();
      return b === this.activeBrandTab;
    });
  }

  companyFilterSearchText: string = '';

  get filteredActiveTabCompanies() {
    let comps = this.activeTabCompanies;
    if (this.companyFilterSearchText) {
      const lower = this.companyFilterSearchText.toLowerCase();
      comps = comps.filter(c => c.company?.toLowerCase().includes(lower));
    }
    return comps;
  }

  get activeFilterCompanyIdsCount() {
    return this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id)).length;
  }

  get isAllActiveCompaniesSelected() {
    const active = this.activeTabCompanies;
    return active.length > 0 && active.every(c => this.filterCompanyIds.has(c.id));
  }

  get isKcegOwnedSelected() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    const activeSelected = this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id));
    return kceg.length > 0 && activeSelected.length === kceg.length && kceg.every(c => this.filterCompanyIds.has(c.id));
  }

  get isGenerateDisabled(): boolean {
    if (this.isGeneratingInvoices) return true;
    if (!this.selectedPeriod || this.selectedPeriod === 'All Periods') return true;

    // Disable for periods before August 2026 (2026/08)
    const parts = this.selectedPeriod.split('/');
    if (parts.length !== 2) return true;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);

    if (isNaN(year) || isNaN(month)) return true;
    if (year < 2026 || (year === 2026 && month < 8)) {
      return true;
    }
    return false;
  }

  switchTab(tab: 'import' | 'management') {
    this.activeTab = tab;
    if (tab === 'import') {
      if (this.selectedPeriod === 'All Periods') {
        this.selectedPeriod = '';
        this.isFileProcessed = false;
        this.importSuccessMessage = '';
      }
    }
  }

  async initCompanyFilters() {
    const user = this.auth.currentUser;
    if (user) {
      const savedIds = await this.invoiceService.getUserInvoicePreferences(user.uid);
      if (savedIds && Array.isArray(savedIds)) {
        // Only keep IDs that currently exist
        const validIds = savedIds.filter(id => this.companies.some(c => c.id === id));

        if (validIds.length > 0) {
          this.filterCompanyIds = new Set(validIds);
        } else {
          // If savedIds was not empty but validIds is empty -> all selected companies were deleted -> Default to All
          // If savedIds was empty -> user selected nothing -> Default to None (empty set)
          if (savedIds.length > 0) {
            this.selectAllCompanies();
          } else {
            this.filterCompanyIds = new Set();
          }
        }
      } else {
        this.selectAllCompanies();
      }
    } else {
      this.selectAllCompanies();
    }
    // Re-apply filters if grid data is already loaded
    if (this.gridData.length > 0) {
      this.applyFilters();
    }
  }

  selectAllCompanies() {
    this.filterCompanyIds = new Set(this.companies.map(c => c.id));
  }

  toggleCompanyFilterDropdown() {
    this.showCompanyFilterDropdown = !this.showCompanyFilterDropdown;
  }

  toggleCountryFilterDropdown() {
    this.showCountryFilterDropdown = !this.showCountryFilterDropdown;
  }

  selectCountryFilter(country: string) {
    this.filterCountry = country;
    this.showCountryFilterDropdown = false;
    this.applyFilters();
  }

  async toggleCompanyFilterItem(companyId: string) {
    if (this.filterCompanyIds.has(companyId)) {
      this.filterCompanyIds.delete(companyId);
    } else {
      this.filterCompanyIds.add(companyId);
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleAllCompanyFilter() {
    const active = this.activeTabCompanies;
    if (this.isAllActiveCompaniesSelected) {
      active.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      active.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleKcegOwnedFilter() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    if (this.isKcegOwnedSelected) {
      kceg.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      this.activeTabCompanies.forEach(c => this.filterCompanyIds.delete(c.id));
      kceg.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async saveCompanyPreferences() {
    const user = this.auth.currentUser;
    if (user) {
      await this.invoiceService.saveUserInvoicePreferences(user.uid, Array.from(this.filterCompanyIds));
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isSavingPDFs) return;

    if (this.isGenerationPreviewOpen) {
      this.cancelGenerationPreview();
      return;
    }

    if (this.generationResult) {
      this.closeGenerationResult();
      return;
    }

    if (this.isManualModalOpen) {
      this.closeManualModal();
      return;
    }

    if (this.isRecipientsModalOpen) {
      this.closeRecipientsModal();
      return;
    }

    if (this.isEmailModalOpen) {
      this.closeEmailModal(true);
      return;
    }

    if (this.duplicateWarningData) {
      this.cancelDuplicate();
      return;
    }

    if (this.isNewStoresModalOpen) {
      this.closeNewStoresModal();
      return;
    }

    if (this.isHistoryModalOpen) {
      this.closeHistoryModal();
      return;
    }

    if (this.isInvoiceModalOpen) {
      this.closeInvoiceModal();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.company-filter-dropdown')) {
      this.showCompanyFilterDropdown = false;
    }
    if (!target.closest('.country-filter-dropdown')) {
      this.showCountryFilterDropdown = false;
    }
  }

  async onPeriodChange(period: string) {
    this.isLoading = true;
    this.selectedPeriod = period;

    if (this.fileProcessedCache.has(period)) {
      this.isFileProcessed = this.fileProcessedCache.get(period)!;
    } else {
      if (period === 'All Periods') {
        this.isFileProcessed = true;
      } else {
        this.isFileProcessed = await this.excelService.isPeriodProcessed(this.selectedPeriod);
      }
      this.fileProcessedCache.set(period, this.isFileProcessed);
    }

    this.importSuccessMessage = '';

    // Always fetch fresh data on period change to reflect unlock states across sessions
    this.gridDataCache.delete(`${period}_${this.activeBrandTab}`);
    await this.loadGridData();
    this.isLoading = false;
  }

  get hasRates(): boolean {
    if (this.selectedPeriod === 'All Periods') {
      return this.rates.length > 0;
    }
    return this.selectedPeriod ? this.getRatesForPeriod(this.selectedPeriod).length > 0 : false;
  }

  getRatesForPeriod(period: string): SmrsRate[] {
    return this.rates
      .filter(r => {
        const b = ((r as any).brand || '').trim();
        return r.period === period && b === this.activeBrandTab;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  getEffectiveStores(periodAggregations: any[]): any[] {
    const tempStores = new Map<string, any>();

    // 1. Add existing stores
    this.stores.forEach(s => {
      const sid = String(s['storeId']).trim();
      if (sid) tempStores.set(sid, s);
    });

    // 2. Add ad-hoc stores from aggregations if they have companyId
    periodAggregations.forEach(agg => {
      const sid = String(agg['storeId'] || '').trim();
      const cid = agg['companyId'];
      if (sid && cid && !tempStores.has(sid)) {
        tempStores.set(sid, { storeId: sid, companyId: cid, companyIds: [cid], isCustom: true, storeName: agg['storeName'] });
      }
    });

    return Array.from(tempStores.values());
  }

  // Helper to normalize store names for robust matching
  private normalizeName(name: string): string {
    return (name || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  async onFileSelected(file: File) {
    if (!this.selectedPeriod || this.selectedPeriod === 'All Periods') {
      alert('Please select a period before uploading.');
      return;
    }

    // Guardrail: Block non-superadmins if future period invoices already exist for this brand
    const user = this.auth.currentUser;
    if (!this.userProfile && user) {
      try {
        const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
        if (profile) this.userProfile = profile;
      } catch (e) {
        console.warn('Could not fetch user profile in onFileSelected', e);
      }
    }

    const brandCompanyIds = this.activeTabCompanies.map(c => c.id);
    const { hasFuture, latestPeriod } = await this.invoiceService.hasFutureInvoices(this.selectedPeriod, brandCompanyIds);
    if (hasFuture && !this.isSuperAdmin) {
      alert(`Cannot import data for ${this.selectedPeriod} because invoice data already exists for a later month (${latestPeriod}). To prevent ledger data corruption, please delete future months first, starting from the latest month.`);
      return;
    }


    this.isImporting = true;
    this.importProgress = 0;
    this.importStatusText = 'Reading and parsing Excel file...';
    this.excelService.resetCancellation();
    this.duplicateWarningData = null;
    this.historicalImportConfirmationData = null;
    this.importSuccessMessage = '';
    this.newlyAddedStores = [];
    this.skippedRows = [];

    try {
      // 1. Read and parse Excel file in memory
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(worksheet);

      // Extract keys for rate customization
      const keyMap = new Map<string, Set<string>>();
      rawRows.forEach((r: any) => {
        const feeType = r['서버비 구분'];
        const ip = r['IP'];
        if (feeType) {
          const key = String(feeType).trim();
          if (!keyMap.has(key)) {
            keyMap.set(key, new Set());
          }
          if (ip) {
            keyMap.get(key)!.add(String(ip).trim());
          }
        }
      });
      this.extractedKeys = Array.from(keyMap.entries())
        .map(([key, ipsSet]) => ({ key, ips: Array.from(ipsSet).sort() }))
        .sort((a, b) => a.key.localeCompare(b.key));

      // Fetch existing stores for smart matching
      const storeMap = new Map<string, string>();
      this.stores.forEach(s => {
        const sid = (s['storeId'] || s['id'] || '').toString().trim();
        if (sid) {
          storeMap.set(this.normalizeName(sid), sid);
        }
        if (s['name']) {
          storeMap.set(this.normalizeName(s['name']), sid || s['name']);
        }
        if (s['storeName']) {
          storeMap.set(this.normalizeName(s['storeName']), sid || s['storeName']);
        }
      });

      // Validate data before processing
      this.validateImportData(rawRows);

      // Calculate aggregations and count locally to get the correct record count for comparison
      const { processedCount, skippedRows, totalPeriodRows } = this.processLocalAggregations(rawRows, storeMap);

      if (totalPeriodRows === 0) {
        throw new Error(`The selected file contains no valid rows matching the period '${this.selectedPeriod}'. Please verify the file and period selection.`);
      }

      if (skippedRows.length > 0) {
        this.skippedRows = skippedRows;
        this.isImporting = false;
        return;
      }

      // Check for duplicates
      this.importStatusText = 'Checking duplicate import records...';
      const duplicateDoc = await this.invoiceService.checkDuplicateImport(this.selectedPeriod, file.name, processedCount);
      console.log(`Checking duplicates for '${file.name}' (${processedCount} processed rows) in period '${this.selectedPeriod}'.`);

      if (duplicateDoc) {
        const duplicate = duplicateDoc.data();
        // Attach the doc ID for deletion
        (duplicate as any).id = duplicateDoc.id;

        this.duplicateWarningData = {
          file: file,
          rows: rawRows,
          existingRecord: duplicate,
          storeMap: storeMap
        };
        this.isImporting = false;
        return; // Stop here, wait for user input
      }

      // Historical Period Check for Superadmin: Show confirmation and impact review before committing
      if (hasFuture && this.isSuperAdmin) {
        this.importStatusText = 'Analyzing affected historical ledgers...';
        const futureDetails = await this.invoiceService.getFutureInvoicesDetails(this.selectedPeriod, brandCompanyIds);
        
        // Find company names detected in the imported rows
        const storeToCompanyMap = new Map<string, string[]>();
        this.stores.forEach(s => {
          const sid = String(s['storeId'] || s['id'] || '').trim();
          if (sid) {
            const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
            storeToCompanyMap.set(sid, cids);
            storeToCompanyMap.set(this.normalizeName(sid), cids);
            if (s['name']) storeToCompanyMap.set(this.normalizeName(s['name']), cids);
            if (s['storeName']) storeToCompanyMap.set(this.normalizeName(s['storeName']), cids);
          }
        });

        const detectedCompanyNamesSet = new Set<string>();
        const { aggregations } = this.processLocalAggregations(rawRows, storeMap);
        Object.keys(aggregations).forEach(storeId => {
          const cids = storeToCompanyMap.get(storeId) || storeToCompanyMap.get(this.normalizeName(storeId)) || [];
          cids.forEach(cid => {
            const comp = this.companies.find(c => c.id === cid);
            if (comp) detectedCompanyNamesSet.add(comp.company);
          });
        });

        this.historicalImportConfirmationData = {
          file: file,
          rawRows: rawRows,
          storeMap: storeMap,
          period: this.selectedPeriod,
          brandName: this.activeBrandName,
          detectedCompanies: Array.from(detectedCompanyNamesSet).sort(),
          affectedFuturePeriods: futureDetails.futurePeriods,
          affectedCompanyCount: detectedCompanyNamesSet.size || futureDetails.affectedCompanyIds.length
        };
        this.isImporting = false;
        return;
      }

      // Proceed with normal import
      await this.executeImport(file, rawRows, undefined, undefined, storeMap);

    } catch (error: any) {
      if (error.code === 'storage/canceled' || (error.message && error.message.includes('cancelled'))) {
        console.log('Import was cancelled by the user.');
        alert('Import cancelled.');
      } else {
        console.error('Import failed:', error);
        alert(error.message || 'An error occurred during the import process.');
      }
      this.isImporting = false;
      this.importProgress = 0;
    }
  }

  async proceedWithHistoricalImport() {
    if (!this.historicalImportConfirmationData) return;

    this.isImporting = true;
    this.importStatusText = 'Committing historical import and recalculating ledgers...';
    const { file, rawRows, storeMap } = this.historicalImportConfirmationData;
    this.historicalImportConfirmationData = null;

    try {
      await this.executeImport(file, rawRows, undefined, undefined, storeMap);
    } catch (error: any) {
      console.error('Historical import failed:', error);
      alert(error.message || 'An error occurred during the historical import process.');
      this.isImporting = false;
    }
  }

  cancelHistoricalImport() {
    this.historicalImportConfirmationData = null;
    this.isImporting = false;
    this.importProgress = 0;
  }

  async proceedWithDuplicate() {
    if (!this.duplicateWarningData) return;

    this.isImporting = true;
    this.importStatusText = 'Processing re-import...';
    const { file, rows, existingRecord, storeMap } = this.duplicateWarningData;
    this.duplicateWarningData = null; // Close modal

    try {
      // Pass the existing record info to delete previous data
      await this.executeImport(file, rows, existingRecord.importId, existingRecord.id, storeMap);
    } catch (error: any) {
      console.error('Import failed:', error);
      alert(error.message || 'An error occurred during the import process.');
      this.isImporting = false;
    }
  }

  cancelDuplicate() {
    this.duplicateWarningData = null;
    this.isImporting = false;
    this.importProgress = 0;
  }

  private async executeImport(file: File, rawRows: any[], previousImportId?: string, previousHistoryId?: string, storeMap?: Map<string, string>) {
    if (!this.selectedPeriod) {
      console.error("executeImport called without a selected period.");
      return;
    }
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';
    this.newlyAddedStores = [];
    this.skippedRows = [];

    const companyDeltas = new Map<string, number>();
    const storeToCompanyMap = new Map<string, string[]>();
    this.stores.forEach(s => {
      const sid = String(s['storeId'] || s['id'] || '').trim();
      if (sid) {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        storeToCompanyMap.set(sid, cids);
        storeToCompanyMap.set(this.normalizeName(sid), cids);
        if (s['name']) storeToCompanyMap.set(this.normalizeName(s['name']), cids);
        if (s['storeName']) storeToCompanyMap.set(this.normalizeName(s['storeName']), cids);
      }
    });

    try {
      this.importStatusText = 'Calculating monthly store aggregations...';
      // 2. Calculate aggregations locally
      const { aggregations, processedCount, skippedRows, totalPeriodRows } = this.processLocalAggregations(rawRows, storeMap);
      this.skippedRows = skippedRows;

      // Create missing stores if storeMap is provided
      if (storeMap) {
        const newStores = [];
        const storesToUpdate = [];

        for (const storeId of Object.keys(aggregations)) {
          const agg = aggregations[storeId];
          const normalizedId = this.normalizeName(storeId);

          if (!storeMap.has(normalizedId)) {
            this.newlyAddedStores.push(storeId);
            const newStore: any = { storeId, storeName: agg.storeName || storeId };
            if (agg.country) newStore.country = agg.country;
            newStores.push(newStore);
            storeMap.set(normalizedId, storeId);
          } else if (agg.country) {
            const existingId = storeMap.get(normalizedId);
            const existingStore = this.stores.find(s => s.storeId === existingId);
            if (existingStore && !existingStore.country) {
              storesToUpdate.push({ id: existingStore.id, storeId: existingId, country: agg.country });
            }
          }
        }
        if (newStores.length > 0) {
          this.importStatusText = `Registering ${newStores.length} new store(s)...`;
          await this.invoiceService.createNewStores(newStores, userName);
        }
        if (storesToUpdate.length > 0) await this.invoiceService.updateStores(storesToUpdate, userName);
      }

      // Generate a new Import ID
      const importId = 'import_' + Date.now();

      // 3. Upload raw file permanently to Firebase Storage under Platform folder
      this.importStatusText = 'Uploading raw Excel backup to Storage...';
      const sanitizedPeriod = this.selectedPeriod.replace('/', '-');
      const filePath = `Platform/Invoices/RawImports/${sanitizedPeriod}/${Date.now()}_${file.name}`;
      const fileRef = ref(this.storage, filePath);
      this.currentUploadTask = uploadBytesResumable(fileRef, file);

      // Track upload progress
      this.currentUploadTask.on('state_changed', (snapshot) => {
        this.importProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        this.importStatusText = `Uploading raw Excel backup (${Math.round(this.importProgress)}%)...`;
      });

      await this.currentUploadTask;
      const downloadURL = await getDownloadURL(fileRef);
      this.currentUploadTask = null; // Upload is done, clear the task.

      if (this.excelService.isCancelled()) {
        throw new Error('Import cancelled by user.');
      }

      // Delete specific previous import if requested (Duplicate flow)
      if (previousImportId) {
        this.importStatusText = 'Removing previous import records...';
        const rates = this.getRatesForPeriod(this.selectedPeriod);
        const deltas = await this.invoiceService.deletePreviousImport(previousImportId, storeToCompanyMap, rates, this.selectedPeriod);
        deltas.forEach((val, key) => companyDeltas.set(key, (companyDeltas.get(key) || 0) + val));
      }

      if (previousHistoryId) {
        await this.invoiceService.deleteHistory(previousHistoryId);
      }

      // 4. Save aggregated results to Firestore using a batch for atomicity
      this.importStatusText = 'Saving monthly aggregations to database...';
      const historyData = {
        fileName: file.name,
        period: this.selectedPeriod,
        importId: importId, // Associate ID
        uploadedAt: new Date().toISOString(),
        uploadedBy: userName,
        downloadURL: downloadURL,
        status: 'Completed',
        recordCount: processedCount
      };

      const rates = this.getRatesForPeriod(this.selectedPeriod);
      const commitPromise = this.invoiceService.saveImportData(aggregations, historyData, this.selectedPeriod, importId, userName, storeToCompanyMap, rates);

      const cancelPromise = firstValueFrom(this.excelService.cancelSignal$).then(() => {
        throw new Error('Import cancelled by user during database write.');
      });

      try {
        const deltas = await Promise.race([commitPromise, cancelPromise]);
        deltas.forEach((val, key) => companyDeltas.set(key, (companyDeltas.get(key) || 0) + val));
      } catch (error: any) {
        if (error.message === 'Import cancelled by user during database write.') {
          await this.invoiceService.cleanupImportData(importId);
        }
        throw error;
      }

      this.importStatusText = 'Updating invoice balances & ledger numbers...';
      await this.invoiceService.processInvoiceUpdates(
        this.selectedPeriod, 
        companyDeltas, 
        importId, 
        this.stores, 
        rates, 
        userName,
        (statusMsg: string) => {
          this.importStatusText = statusMsg;
        }
      );

      this.isFileProcessed = true;
      this.cachedAggregations = [];
      this.gridDataCache.clear();
      this.rawDataCache.clear();
      this.fileProcessedCache.clear();
      if (this.skippedRows.length > 0) {
        this.importSuccessMessage = `Import was partially done.\n(${processedCount} out of ${totalPeriodRows} rows for ${this.selectedPeriod} processed)`;
      } else {
        this.importSuccessMessage = `Import completed successfully!\n(${processedCount} out of ${totalPeriodRows} rows for ${this.selectedPeriod} processed)`;
      }

      if (this.rateCustomization) {
        this.rateCustomization.clearCache();
      }

      this.loadGridData();
    } finally {
      this.isImporting = false;
      this.importProgress = 0;
      this.currentUploadTask = null;
    }
  }

  private validateImportData(rows: any[]) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row['기간']) {
        throw new Error(`Row ${i + 2} is missing a date ('기간')`);
      }

      const dateObj = new Date(row['기간']);
      if (isNaN(dateObj.getTime())) {
        throw new Error(`Row ${i + 2} has an invalid date`);
      }
    }
  }

  private processLocalAggregations(rows: any[], storeMap?: Map<string, string>) {
    const aggs: any = {};
    let processedCount = 0;
    let totalPeriodRows = 0;
    const skippedRows: any[] = [];

    rows.forEach((row, index) => {
      // Filter by period first
      if (!row['기간']) return;
      const dateObj = new Date(row['기간']);
      if (isNaN(dateObj.getTime())) return;

      // Use local time to prevent timezone shifts (e.g. 00:10 -> previous day UTC)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const rowPeriod = `${year}/${month}`;

      if (rowPeriod !== this.selectedPeriod) return;
      totalPeriodRows++;

      const rawStoreId = row['지점 계정'];
      if (!rawStoreId) {
        skippedRows.push({
          rowNumber: index + 2,
          reason: 'Missing Store ID'
        });
        return;
      }

      let storeId = String(rawStoreId).trim();

      if (storeMap) {
        const normalized = this.normalizeName(storeId);
        if (storeMap.has(normalized)) {
          storeId = storeMap.get(normalized)!;
        }
      }

      processedCount++;

      if (!aggs[storeId]) {
        aggs[storeId] = { revenues: {}, ips: {} };
      }
      if (row['지점명']) {
        aggs[storeId].storeName = String(row['지점명']).trim();
      }

      const rawCountry = row['국가 구분'];
      if (rawCountry) {
        let country = String(rawCountry).trim();
        if (country === '미국2') {
          country = '미국';
        }
        aggs[storeId].country = country;
      }

      const amount = Number(row['매출액'] || 0);
      const feeType = row['서버비 구분'];
      const ip = row['IP'];

      // Store by raw feeType (import key) instead of mapping to rate ID immediately
      // This allows dynamic recalculation if rate mappings change later
      if (feeType) {
        let key = String(feeType).trim();
        aggs[storeId].revenues[key] = (aggs[storeId].revenues[key] || 0) + amount;

        if (ip) {
          if (!aggs[storeId].ips[key]) aggs[storeId].ips[key] = {};
          aggs[storeId].ips[key][ip] = (aggs[storeId].ips[key][ip] || 0) + amount;
        }
      }
    });
    return { aggregations: aggs, processedCount, skippedRows, totalPeriodRows };
  }

  cancelImport() {
    if (confirm('Are you sure you want to cancel the import?')) {
      this.currentUploadTask?.cancel();
      this.excelService.cancelImport(); // This will trigger the cancelSignal$
      this.isFileProcessed = false;
      this.isImporting = false;
      this.showUploader = false;
      setTimeout(() => {
        this.showUploader = true;
      }, 0);
    }
  }

  async onDeleteHistory(event: any) {
    // Handle both object with period property (from grid) or direct string
    const period = event?.period || event;

    if (!period || typeof period !== 'string') {
      console.error('Invalid period received for deletion:', event);
      return;
    }

    // Guardrail: Block deletion if future period invoices exist for this brand
    const brandCompanyIds = this.activeTabCompanies.map(c => c.id);
    const { hasFuture, latestPeriod } = await this.invoiceService.hasFutureInvoices(period, brandCompanyIds);
    if (hasFuture) {
      alert(`Cannot delete data for ${period} because invoice data already exists for a later month (${latestPeriod}). To prevent ledger data corruption, please delete future months first, starting from the latest month.`);
      return;
    }

    try {
      await this.excelService.deleteImport(period);

      if (this.rateCustomization) {
        this.rateCustomization.clearCache();
      }

      // Clean up invoices for this period
      await this.invoiceService.deleteInvoiceHistory(period);

      if (this.selectedPeriod === period) {
        this.isFileProcessed = false;
      }

      this.cachedAggregations = [];
      this.gridDataCache.clear();
      this.rawDataCache.clear();
      this.fileProcessedCache.clear();
      await this.loadGridData();
    } catch (error) {
      console.error('Error deleting invoice history:', error);
      alert('Failed to delete invoice data. Please check console for details.');
    }
  }

  async getBeginningBalance(companyId: string, currentPeriod: string): Promise<number> {
    return this.invoiceService.getBeginningBalance(companyId, currentPeriod);
  }

  async toggleCompanyExpansion(row: any) {
    if (this.expandedRowId === row.id) {
      this.expandedRowId = null;
      this.expandedCompanyTotals = null;
      return;
    }

    this.expandedRowId = row.id;
    this.expandedCompanyTotals = null;
    this.editingValues = {};

    await this.fetchCompanyDetails(row);
  }

  async fetchCompanyDetails(row: any) {
    // 1. Get stores for this company
    const company = row.company;
    const uniqueStores = new Set<string>();
    this.stores.forEach(s => {
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      if (cids.includes(company.id)) {
        uniqueStores.add(String(s['storeId']).trim());
      }
    });

    // Add stores from aggregations that belong to this company (custom stores)
    const aggDocsData = this.cachedAggregations.filter(d => d['period'] === row.period);
    aggDocsData.forEach(d => {
      if (d['companyId'] === company.id && d['storeId']) {
        uniqueStores.add(String(d['storeId']).trim());
      }
    });

    // 2. Aggregate data

    const totals: any = { revenues: {}, fees: {} };
    const rates = this.getRatesForPeriod(row.period);
    rates.forEach(r => {
      if (r.id) {
        totals.revenues[r.id] = 0;
        totals.fees[r.id] = 0;
      }
    });

    const aggDocs: any[] = [];

    aggDocsData.forEach((d: any) => {
      const sid = String(d['storeId'] || '').trim();
      if (uniqueStores.has(sid) && d['revenues']) {
        aggDocs.push({ data: d });
        rates.forEach(r => {
          if (r.id) {
            const val = this.calculateRevenueForRate(d, r);
            totals.revenues[r.id] = (totals.revenues[r.id] || 0) + val;
          }
        });
      }
    });

    let grandTotalRevenue = 0;
    let totalFee = 0;
    const kcegFees: { [key: string]: number } = {};
    const lfcFees: { [key: string]: number } = {};
    const kcegRatesMap: { [key: string]: number } = {};
    const lfcRatesMap: { [key: string]: number } = {};
    let totalKcegFee = 0;
    let totalLfcFee = 0;

    rates.forEach(r => {
      if (r.id) {
        const rev = totals.revenues[r.id] || 0;
        const fee = company.isFeeCollection ? 0 : (rev * (r.rate / 100));
        totals.fees[r.id] = fee;
        grandTotalRevenue += rev;
        totalFee += fee;

        const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
        const lfcRateVal = (r.rate || 0) - kcegRateVal;

        let kcegFee = 0;
        let lfcFee = 0;
        if ((r.rate || 0) !== 0) {
          kcegFee = fee * (kcegRateVal / r.rate);
          lfcFee = fee * (lfcRateVal / r.rate);
        }

        kcegFees[r.id] = kcegFee;
        lfcFees[r.id] = lfcFee;
        kcegRatesMap[r.id] = kcegRateVal;
        lfcRatesMap[r.id] = lfcRateVal;
        totalKcegFee += kcegFee;
        totalLfcFee += lfcFee;
      }
    });

    totalFee = Math.round(totalFee * 100) / 100;
    let beginningBalance = 0;
    if (company.isFeeCollection) {
      beginningBalance = 0;
    } else if (row.beginningBalanceOverride !== undefined) {
      beginningBalance = row.beginningBalanceOverride;
    } else {
      beginningBalance = Math.round((row.totalDue - row.totalFee) * 100) / 100;
    }
    const totalDue = company.isFeeCollection ? 0 : Math.round((beginningBalance + totalFee) * 100) / 100;

    const feeCollectionBreakdown: any[] = [];
    if (company.isFeeCollection) {
      aggDocs.forEach((doc: any) => {
        const d = doc.data;
        const cName = d.sourceCompanyName || 'Other / Legacy Transfer';
        let amount = 0;
        rates.forEach(r => {
          if (r.id) {
            amount += this.calculateRevenueForRate(d, r);
          }
        });

        const lfcFee = d.sourceLfcFee || 0;
        const kcegFee = d.sourceKcegFee || 0;

        if (amount !== 0) {
          totalLfcFee += lfcFee;
          totalKcegFee += kcegFee;

          const date = d.updatedAt || null;
          let status = 'Paid';

          if (d.sourceCompanyId) {
            const sourceRow = this.gridData.find(r => r.company.id === d.sourceCompanyId && r.period === row.period);
            if (sourceRow) {
              status = sourceRow.status;
            }
          }

          const existing = feeCollectionBreakdown.find((x: any) => x.companyName === cName);
          if (existing) {
            existing.amount += amount;
            existing.lfcFee += lfcFee;
            existing.kcegFee += kcegFee;
            if (date && (!existing.date || date > existing.date)) {
              existing.date = date;
            }
            existing.status = status;
          } else {
            feeCollectionBreakdown.push({ companyName: cName, amount, lfcFee, kcegFee, date, status });
          }
        }
      });
    }

    const filteredFeeCollectionBreakdown = feeCollectionBreakdown.filter(x => Math.abs(x.amount) > 0.01);

    const exchangeRate = row.exchangeRate || 1;
    const exchangeRateDate = row.exchangeRateDate;
    const grandTotalRevenueUSD = grandTotalRevenue * exchangeRate;
    const totalFeeUSD = totalFee * exchangeRate;
    const totalLfcFeeUSD = totalLfcFee * exchangeRate;
    const totalKcegFeeUSD = totalKcegFee * exchangeRate;
    const beginningBalanceUSD = beginningBalance * exchangeRate;
    const totalDueUSD = totalDue * exchangeRate;
    const remainingBalanceUSD = row.remainingBalanceUSD;

    this.expandedCompanyTotals = {
      ...totals,
      grandTotalRevenue,
      totalFee,
      beginningBalance, totalDue,
      rates, // Store rates for template
      aggDocs,
      feeCollectionBreakdown: filteredFeeCollectionBreakdown,
      kcegFees,
      lfcFees,
      kcegRatesMap,
      lfcRatesMap,
      totalKcegFee,
      totalLfcFee,
      isEditingBeginningBalance: false,
      newBeginningBalance: beginningBalance,
      isOverridden: row.beginningBalanceOverride !== undefined,
      grandTotalRevenueUSD,
      totalFeeUSD,
      totalLfcFeeUSD,
      totalKcegFeeUSD,
      beginningBalanceUSD,
      totalDueUSD,
      remainingBalanceUSD,
      exchangeRate,
      exchangeRateDate,
      isInternational: row.isInternational,
      currency: row.currency
    };
  }

  calculateDashboardStats() {
    const stats = {
      totalRevenue: 0,
      totalFees: 0,
      totalDue: 0,
      totalCollected: 0,
      totalOutstanding: 0,
      collectionRate: 0,
      statusCounts: { Paid: 0, Partial: 0, 'Not Paid': 0, Canceled: 0 } as Record<string, number>
    };

    this.filteredGridData.forEach(row => {
      if (row.company?.isFeeCollection) return;

      const status = row.status || 'Not Paid';
      stats.statusCounts[status] = (stats.statusCounts[status] || 0) + 1;

      if (status !== 'Canceled') {
        stats.totalRevenue += (row.totalRevenueUSD || 0);
        stats.totalFees += (row.totalFeeUSD || 0);
        stats.totalDue += (row.totalDueUSD || 0);
        stats.totalCollected += (row.amountPaidUSD || 0);
        stats.totalOutstanding += (row.remainingBalanceUSD || 0);
      }
    });

    if (stats.totalDue > 0) {
      stats.collectionRate = (stats.totalCollected / stats.totalDue) * 100;
    }

    this.dashboardStats = stats;
    this.calculateIncentiveStats();
  }

  private async loadSinglePeriodData(period: string, silent: boolean = false) {
    this.savedScrollPosition = window.scrollY;
    this.shouldRestoreScroll = true;
    if (!silent) {
      this.isLoadingGrid = true;
      this.gridData = [];
    }

    try {
      // 1. Fetch Aggregations for the selected period
      const aggDocs = await this.invoiceService.getAggregations(period);
      this.cachedAggregations = aggDocs;
      this.rawDataCache.set(period, aggDocs);
      const effectiveStores = this.getEffectiveStores(aggDocs);
      const aggDataMap = this.invoiceService.calculateRevenueAndFees(aggDocs, effectiveStores, this.rates, period);

      // 2. Fetch Invoices for the selected period
      const invDocs = await this.invoiceService.getInvoices(period);
      const statusMap = new Map<string, any>();
      invDocs.forEach((d: any) => {
        const cid = d['companyId'] || d['company']?.id || (typeof d['company'] === 'string' ? d['company'] : '');
        const cName = (d['company'] && typeof d['company'] === 'object' ? d['company'].company : '') || d['companyName'] || '';
        const isManuallyUnlocked = d['isManuallyUnlocked'] === true || d['isManuallyUnlocked'] === 'true';

        const invData = {
          status: d['status'] || 'Not Paid',
          emailStatus: d['emailStatus'] || 'Not Sent',
          actualAmountPaid: d['actualAmountPaid'],
          appliedFrom: d['appliedFrom'] || [],
          emailSentAt: d['emailSentAt'],
          remainingBalance: d['remainingBalance'],
          amountPaid: d['amountPaid'],
          beginningBalance: d['beginningBalance'],
          totalFee: d['totalFee'],
          totalDue: d['totalDue'],
          totalRevenue: d['totalRevenue'],
          totalLfcFee: d['totalLfcFee'],
          totalKcegFee: d['totalKcegFee'],
          emailRecipients: d['emailRecipients'],
          isManual: d['isManual'] || false,
          isEdited: d['isEdited'] || false,
          isManuallyUnlocked: isManuallyUnlocked,
          beginningBalanceOverride: d['beginningBalanceOverride'],
          docId: d.id,
          createdAt: d['createdAt'],
          createdBy: d['createdBy'],
          updatedAt: d['updatedAt'],
          updatedBy: d['updatedBy']
        };

        if (cid) statusMap.set(cid, invData);
        if (d['companyId']) statusMap.set(d['companyId'], invData);
        if (cName) statusMap.set(cName, invData);
        if (d.id) statusMap.set(d.id, invData);
      });

      // Pre-calculate company countries
      this.companyCountryMap.clear();
      this.companyCurrencyMap.clear();
      this.stores.forEach(s => {
        const country = s['country'] || '미국';
        const currency = s['currency'] || (['Canada', '캐나다'].includes(country) ? 'CAD' : 'USD');
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        cids.forEach((cid: string) => {
          const current = this.companyCountryMap.get(cid);
          if (country !== '미국') {
            this.companyCountryMap.set(cid, country);
            this.companyCurrencyMap.set(cid, currency);
          } else if (!current) {
            this.companyCountryMap.set(cid, '미국');
            this.companyCurrencyMap.set(cid, 'USD');
          }
        });
      });

      // 3. Build Grid Data
      const rows: any[] = [];
      const promises = this.activeTabCompanies.map(async company => {
        const key = `${period}_${company.id}`;
        const agg = aggDataMap.get(key);
        const stat = statusMap.get(company.id);

        if (!agg && !stat) return;

        const beginningBalance = company.isFeeCollection ? 0 : 
          (stat?.beginningBalanceOverride ?? stat?.beginningBalance ?? await this.invoiceService.getBeginningBalance(company.id, period));
        const totalFee = company.isFeeCollection ? 0 : 
          ((stat?.totalFee !== undefined && stat?.totalFee > 0) ? stat.totalFee : (agg ? Math.round(agg.fee * 100) / 100 : (stat?.totalFee || 0)));
        const totalLfcFee = company.isFeeCollection ? 0 : 
          ((stat?.totalLfcFee !== undefined && stat?.totalLfcFee > 0) ? stat.totalLfcFee : (agg ? Math.round(agg.lfcFee * 100) / 100 : (stat?.totalLfcFee || 0)));
        const totalKcegFee = company.isFeeCollection ? 0 : 
          ((stat?.totalKcegFee !== undefined && stat?.totalKcegFee > 0) ? stat.totalKcegFee : (agg ? Math.round(agg.kcegFee * 100) / 100 : (stat?.totalKcegFee || 0)));
        const totalRevenue = (stat?.totalRevenue !== undefined && stat?.totalRevenue > 0) ? stat.totalRevenue : (agg ? agg.revenue : (stat?.totalRevenue || 0));
        const totalDue = company.isFeeCollection ? 0 : 
          Math.round((totalFee + beginningBalance) * 100) / 100;

        let amountPaid = 0;
        let remainingBalance = totalDue;

        if (company.isFeeCollection) {
          remainingBalance = 0;
          amountPaid = 0;
        } else if (stat) {
          amountPaid = stat.amountPaid !== undefined ? stat.amountPaid : 0;
          remainingBalance = stat.remainingBalance !== undefined ? stat.remainingBalance : Math.round((totalDue - amountPaid) * 100) / 100;
        }

        const actualAmountPaid = stat?.actualAmountPaid;
        const displayAmountPaid = actualAmountPaid !== undefined ? actualAmountPaid : amountPaid;

        const country = this.companyCountryMap.get(company.id) || '미국';
        const currency = this.companyCurrencyMap.get(company.id) || 'USD';
        const isInternational = currency !== 'USD';
        let exchangeRate = 1;
        let exchangeRateDate: Date | undefined;
        if (isInternational) {
          const rateData = await this.getExchangeRate(currency, period);
          exchangeRate = rateData.rate;
          exchangeRateDate = rateData.date;
        }

        const rowData = { id: key, company, country, period, beginningBalance, totalRevenue: totalRevenue, totalFee, totalLfcFee, totalKcegFee, totalDue, remainingBalance, amountPaid, actualAmountPaid, displayAmountPaid, appliedFrom: stat?.appliedFrom || [], status: stat ? stat.status : 'Not Paid', emailStatus: stat ? stat.emailStatus : 'Not Sent', emailSentAt: stat?.emailSentAt ? (stat.emailSentAt.toDate ? stat.emailSentAt.toDate() : new Date(stat.emailSentAt)) : null, emailRecipients: stat ? stat.emailRecipients : null, isManual: stat ? stat.isManual : false, isEdited: stat ? stat.isEdited : false, isManuallyUnlocked: stat ? (stat.isManuallyUnlocked || false) : false, beginningBalanceOverride: stat ? stat.beginningBalanceOverride : undefined, invoiceDocId: stat ? stat.docId : null, createdAt: stat?.createdAt ? (stat.createdAt.toDate ? stat.createdAt.toDate() : new Date(stat.createdAt)) : null, createdBy: stat?.createdBy, updatedAt: stat?.updatedAt ? (stat.updatedAt.toDate ? stat.updatedAt.toDate() : new Date(stat.updatedAt)) : null, updatedBy: stat?.updatedBy, totalRevenueUSD: totalRevenue * exchangeRate, totalFeeUSD: totalFee * exchangeRate, totalDueUSD: totalDue * exchangeRate, remainingBalanceUSD: remainingBalance * exchangeRate, amountPaidUSD: amountPaid * exchangeRate, exchangeRate, exchangeRateDate, isInternational, currency };
        rows.push(rowData);
      });

      await Promise.all(promises);

      rows.sort((a, b) => {
        if (a.company.isFeeCollection && !b.company.isFeeCollection) return -1;
        if (!a.company.isFeeCollection && b.company.isFeeCollection) return 1;
        return (a.company.company || '').localeCompare(b.company.company || '');
      });

      this.gridData = rows;
      this.gridDataCache.set(`${period}_${this.activeBrandTab}`, this.gridData);
      this.applyFilters();
      await this.recalculateLocks();
    } catch (err) {
      console.error('Error loading single period data:', err);
    } finally {
      if (!silent) {
        this.isLoadingGrid = false;
      }
      this.cd.detectChanges();
    }
  }

  async loadGridData(silent: boolean = false) {
    this.lastLoadedPeriod = this.selectedPeriod;
    if (!this.selectedPeriod || this.companies.length === 0) {
      this.gridData = [];
      this.isLoadingGrid = false;
      return;
    }

    if (this.selectedPeriod !== 'All Periods') {
      await this.loadSinglePeriodData(this.selectedPeriod, silent);
      return;
    }

    this.savedScrollPosition = window.scrollY;
    this.shouldRestoreScroll = true;
    if (!silent) {
      this.isLoadingGrid = true;
      this.gridData = [];
    }

    try {
      // 2. Fetch Aggregations
      const aggDocs = await this.invoiceService.getAggregations(this.selectedPeriod);
      this.cachedAggregations = aggDocs;
      this.rawDataCache.set(this.selectedPeriod, aggDocs);
      const effectiveStores = this.getEffectiveStores(aggDocs);
      const aggDataMap = this.invoiceService.calculateRevenueAndFees(aggDocs, effectiveStores, this.rates, this.selectedPeriod);

      // 3. Fetch Invoices (Statuses)
      // Always fetch all invoices to ensure we can calculate running balances across gaps
      const statusMap = new Map<string, { status: string, emailStatus: string, emailSentAt: any, remainingBalance: number, amountPaid?: number, beginningBalance?: number, totalFee?: number, totalDue?: number, totalRevenue?: number, totalLfcFee?: number, totalKcegFee?: number, actualAmountPaid?: number, appliedFrom?: any[], emailRecipients?: string, isManual?: boolean, isEdited?: boolean, isManuallyUnlocked?: boolean, beginningBalanceOverride?: number, docId: string, createdAt?: any, createdBy?: string, updatedAt?: any, updatedBy?: string }>();
      const invDocs = await this.invoiceService.getInvoices(); // Fetches all fields including createdAt, createdBy, etc.
      invDocs.forEach((d: any) => {
        const cid = d['companyId'] || d['company']?.id || (typeof d['company'] === 'string' ? d['company'] : '');
        if (d['period']) {
          const invData = {
            status: d['status'] || 'Not Paid',
            emailStatus: d['emailStatus'] || 'Not Sent',
            actualAmountPaid: d['actualAmountPaid'],
            appliedFrom: d['appliedFrom'] || [],
            emailSentAt: d['emailSentAt'],
            remainingBalance: d['remainingBalance'],
            amountPaid: d['amountPaid'],
            beginningBalance: d['beginningBalance'],
            totalFee: d['totalFee'],
            totalDue: d['totalDue'],
            totalRevenue: d['totalRevenue'],
            totalLfcFee: d['totalLfcFee'],
            totalKcegFee: d['totalKcegFee'],
            emailRecipients: d['emailRecipients'],
            isManual: d['isManual'] || false,
            isEdited: d['isEdited'] || false,
            isManuallyUnlocked: d['isManuallyUnlocked'] || false,
            beginningBalanceOverride: d['beginningBalanceOverride'],
            docId: d.id,
            createdAt: d['createdAt'],
            createdBy: d['createdBy'],
            updatedAt: d['updatedAt'],
            updatedBy: d['updatedBy']
          };
          if (cid) {
            statusMap.set(`${d['period']}_${cid}`, invData);
          }
          if (d['companyId'] && d['companyId'] !== cid) {
            statusMap.set(`${d['period']}_${d['companyId']}`, invData);
          }
        }
      });

      // 4. Build Grid Data
      const rows: any[] = [];

      // Pre-calculate company countries
      this.companyCountryMap.clear();
      this.companyCurrencyMap.clear();
      this.stores.forEach(s => {
        const country = s['country'] || '미국';
        const currency = s['currency'] || (['Canada', '캐나다'].includes(country) ? 'CAD' : 'USD');
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        cids.forEach((cid: string) => {
          const current = this.companyCountryMap.get(cid);
          if (country !== '미국') {
            this.companyCountryMap.set(cid, country);
            this.companyCurrencyMap.set(cid, currency);
          } else if (!current) {
            this.companyCountryMap.set(cid, '미국');
            this.companyCurrencyMap.set(cid, 'USD');
          }
        });
      });

      // Process chronologically (Oldest to Newest) to track running balances
      const chronologicalPeriods = [...this.periods].reverse();
      const companyRunningBalances = new Map<string, number>();

      // Initialize running balances from invoices older than the start period
      const startPeriod = chronologicalPeriods[0];
      const preStartBalances = new Map<string, { period: string, balance: number }>();

      invDocs.forEach((d: any) => {
        const p = d['period'] ? String(d['period']).trim() : '';
        if (p && p < startPeriod && d['companyId']) {
          const cid = d['companyId'];
          const current = preStartBalances.get(cid);
          if (!current || p > current.period) {
            const bal = d['status'] === 'Canceled' ? 0 : (Number(d['remainingBalance']) || 0);
            preStartBalances.set(cid, { period: p, balance: bal });
          }
        }
      });

      preStartBalances.forEach((val, key) => {
        companyRunningBalances.set(key, val.balance);
      });

      for (const period of chronologicalPeriods) {
        for (const company of this.activeTabCompanies) {
          const key = `${period}_${company.id}`;
          const agg = aggDataMap.get(key);
          const stat = statusMap.get(key);

          // Read persisted values first, falling back to running calculation for un-migrated invoices
          const beginningBalance = company.isFeeCollection ? 0 : 
            (stat?.beginningBalanceOverride !== undefined ? stat.beginningBalanceOverride : 
             (stat?.beginningBalance !== undefined ? stat.beginningBalance : (companyRunningBalances.get(company.id) || 0)));

          const totalFee = company.isFeeCollection ? 0 : 
            ((stat?.totalFee !== undefined && stat?.totalFee > 0) ? stat.totalFee : (agg ? Math.round(agg.fee * 100) / 100 : (stat?.totalFee || 0)));
          const totalLfcFee = company.isFeeCollection ? 0 : 
            ((stat?.totalLfcFee !== undefined && stat?.totalLfcFee > 0) ? stat.totalLfcFee : (agg ? Math.round(agg.lfcFee * 100) / 100 : (stat?.totalLfcFee || 0)));
          const totalKcegFee = company.isFeeCollection ? 0 : 
            ((stat?.totalKcegFee !== undefined && stat?.totalKcegFee > 0) ? stat.totalKcegFee : (agg ? Math.round(agg.kcegFee * 100) / 100 : (stat?.totalKcegFee || 0)));
          const totalRevenue = (stat?.totalRevenue !== undefined && stat?.totalRevenue > 0) ? stat.totalRevenue : (agg ? agg.revenue : (stat?.totalRevenue || 0));
          const totalDue = company.isFeeCollection ? 0 : 
            Math.round((totalFee + beginningBalance) * 100) / 100;

          let amountPaid = 0;
          let remainingBalance = totalDue;

          if (company.isFeeCollection) {
            remainingBalance = 0;
            amountPaid = 0;
          } else if (stat) {
            amountPaid = stat.amountPaid !== undefined ? stat.amountPaid : 0;
            remainingBalance = stat.remainingBalance !== undefined ? stat.remainingBalance : Math.round((totalDue - amountPaid) * 100) / 100;
          }

          // Update running balance for next period fallback
          if (stat && stat.status === 'Canceled') {
            companyRunningBalances.set(company.id, 0);
          } else {
            companyRunningBalances.set(company.id, remainingBalance);
          }

          const country = this.companyCountryMap.get(company.id) || '미국';
          const currency = this.companyCurrencyMap.get(company.id) || 'USD';
          const isInternational = currency !== 'USD';
          let exchangeRate = 1;
          let exchangeRateDate: Date | undefined;
          if (isInternational) {
            const rateData = await this.getExchangeRate(currency, period);
            exchangeRate = rateData.rate;
            exchangeRateDate = rateData.date;
          }

          const actualAmountPaid = stat?.actualAmountPaid;
          const displayAmountPaid = actualAmountPaid !== undefined ? actualAmountPaid : amountPaid;

          if (stat || (agg && agg.revenue !== 0)) {
            rows.push({
              id: key,
              company: company,
              country: country,
              period: period,
              beginningBalance: beginningBalance,
              totalRevenue: totalRevenue,
              totalFee: totalFee,
              totalLfcFee: totalLfcFee,
              totalKcegFee: totalKcegFee,
              totalDue: totalDue,
              remainingBalance: remainingBalance,
              amountPaid: amountPaid,
              actualAmountPaid: actualAmountPaid,
              displayAmountPaid: displayAmountPaid,
              appliedFrom: stat?.appliedFrom || [],
              status: stat ? stat.status : 'Not Paid',
              emailStatus: stat ? stat.emailStatus : 'Not Sent',
              emailSentAt: stat?.emailSentAt ? (stat.emailSentAt.toDate ? stat.emailSentAt.toDate() : new Date(stat.emailSentAt)) : null,
              emailRecipients: stat ? stat.emailRecipients : null,
              isManual: stat ? stat.isManual : false,
              isEdited: stat ? stat.isEdited : false,
              isManuallyUnlocked: stat ? (stat.isManuallyUnlocked || false) : false,
              beginningBalanceOverride: stat ? stat.beginningBalanceOverride : undefined,
              invoiceDocId: stat ? stat.docId : null,
              createdAt: stat?.createdAt ? (stat.createdAt.toDate ? stat.createdAt.toDate() : new Date(stat.createdAt)) : null,
              createdBy: stat?.createdBy,
              updatedAt: stat?.updatedAt ? (stat.updatedAt.toDate ? stat.updatedAt.toDate() : new Date(stat.updatedAt)) : null,
              updatedBy: stat?.updatedBy,
              // USD Values for Grid & KPIs
              totalRevenueUSD: totalRevenue * exchangeRate,
              totalFeeUSD: totalFee * exchangeRate,
              totalDueUSD: totalDue * exchangeRate,
              remainingBalanceUSD: remainingBalance * exchangeRate,
              amountPaidUSD: amountPaid * exchangeRate,
              exchangeRate: exchangeRate,
              exchangeRateDate: exchangeRateDate,
              isInternational: isInternational,
              currency: currency
            });
          }
        }
      }

      // Filter and Sort for View
      if (this.selectedPeriod === 'All Periods') {
        rows.sort((a, b) => {
          if (a.period > b.period) return -1;
          if (a.period < b.period) return 1;
          if (a.company.isFeeCollection && !b.company.isFeeCollection) return -1;
          if (!a.company.isFeeCollection && b.company.isFeeCollection) return 1;
          return (a.company.company || '').localeCompare(b.company.company || '');
        });
        this.gridData = rows;
      } else {
        this.gridData = rows.filter(r => r.period === this.selectedPeriod).sort((a, b) => {
          if (a.company.isFeeCollection && !b.company.isFeeCollection) return -1;
          if (!a.company.isFeeCollection && b.company.isFeeCollection) return 1;
          return (a.company.company || '').localeCompare(b.company.company || '');
        });
      }

      if (this.gridData.length > 0) {
        this.isFileProcessed = true;
      }

      const countries = new Set<string>();
      this.gridData.forEach(r => {
        if (r.country) countries.add(r.country);
      });
      this.availableCountries = Array.from(countries).sort();

      this.gridDataCache.set(`${this.selectedPeriod}_${this.activeBrandTab}`, this.gridData);
      this.applyFilters();
      await this.recalculateLocks();
    } catch (err) {
      console.error('Error loading grid data:', err);
    } finally {
      if (!silent) {
        this.isLoadingGrid = false;
      }
      this.cd.detectChanges();
    }
  }

  private async generateInvoiceData(company: any, period: string) {
    this.isLoadingInvoice = true;
    this.invoiceItems = [];
    this.currentViewRates = this.getRatesForPeriod(period);

    // Deduplicate stores to ensure unique rows in invoice
    const uniqueStores = new Map<string, any>();
    this.stores.forEach(s => {
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      if (cids.includes(company.id) || s['company'] === company.company) {
        uniqueStores.set(String(s['storeId'] || s.id).trim(), s);
      }
    });

    // Also fetch store documents via storeService to be 100% thorough
    const directCompanyStores = await this.storeService.getStoresByCompanyId(company.id);
    directCompanyStores.forEach(s => {
      uniqueStores.set(String(s['storeId'] || s.id).trim(), s);
    });

    // Add ad-hoc stores from aggregations
    const periodAggs = this.cachedAggregations.filter(d => d['period'] === period);
    periodAggs.forEach(d => {
      if (d['companyId'] === company.id && d['storeId'] && !d['isAdjustment']) {
        const sid = String(d['storeId']).trim();
        if (!uniqueStores.has(sid)) uniqueStores.set(sid, { storeId: sid, companyId: company.id, storeName: d['storeName'] });
      }
    });

    const stores = Array.from(uniqueStores.values());

    const periodKey = period.replace('/', '-');
    const items = [];

    // Determine International Status
    let isInternational = false;
    let country = '미국';
    let currency = 'USD';

    for (const store of stores) {
      if (store['country'] && store['country'] !== '미국') {
        isInternational = true;
        country = store['country'];
        currency = store['currency'] || 'USD';
        break; // Assume single country per company invoice
      }
    }

    for (const store of stores) {
      const storeId = String(store['storeId'] || store['id'] || '').trim();
      if (!storeId) continue;

      const storeAggs = periodAggs.filter(d => String(d['storeId']).trim() === storeId && !d['isAdjustment']);

      // Sum up aggregations from multiple imports
      const revenues: { [key: string]: number } = {};
      this.currentViewRates.forEach(r => {
        if (r.id) revenues[r.id] = 0;
      });

      storeAggs.forEach((d: any) => {
        if (d['revenues']) {
          // Map raw keys to rate IDs for the invoice view
          this.currentViewRates.forEach(r => {
            if (r.id) {
              const val = this.calculateRevenueForRate(d, r);
              revenues[r.id] = (revenues[r.id] || 0) + val;
            }
          });
        }
      });

      items.push({
        storeName: store['name'] || store['storeName'] || store['storeId'] || store['id'],
        revenues: revenues
      });
    }

    // Add adjustments at the bottom
    const adjustments = periodAggs.filter(d => d['isAdjustment'] && d['companyId'] === company.id);
    adjustments.forEach((d: any) => {
      const revenues: { [key: string]: number } = {};
      this.currentViewRates.forEach(r => {
        if (r.id && d['revenues'] && d['revenues'][r.id]) {
          revenues[r.id] = Number(d['revenues'][r.id]) || 0;
        }
      });
      items.push({ storeName: d['storeId'], revenues, isAdjustment: true });
    });

    this.invoiceItems = items;

    // Try to get beginning balance from grid data to avoid fetch
    let beginningBalance = 0;
    if (company.isFeeCollection) {
      beginningBalance = 0;
    } else {
      const row = this.gridData.find(r => r.id === `${period}_${company.id}`);
      if (row) {
        if (row.beginningBalanceOverride !== undefined) {
          beginningBalance = row.beginningBalanceOverride;
        } else if (row.beginningBalance !== undefined) {
          beginningBalance = row.beginningBalance;
        } else {
          beginningBalance = Math.round((row.totalDue - row.totalFee) * 100) / 100;
        }
      } else {
        beginningBalance = await this.getBeginningBalance(company.id, period);
      }
    }

    this.calculateTotals(beginningBalance);

    // Apply International Settings
    this.invoiceTotals.isInternational = isInternational;
    this.invoiceTotals.country = country;
    this.invoiceTotals.currency = currency;

    if (isInternational) {
      const rateData = await this.getExchangeRate(currency, period);
      this.invoiceTotals.exchangeRate = rateData.rate;
      this.invoiceTotals.exchangeRateDate = rateData.date;
      // Assuming exchange rate is "USD per 1 Unit of Foreign Currency" (Multiplier)
      // If the rate is like 1300 KRW = 1 USD, then we should divide. 
      // For safety, let's assume the rate stored is the multiplier to get USD.
      this.invoiceTotals.totalDueUSD = this.invoiceTotals.totalDue * this.invoiceTotals.exchangeRate;
    }


    // Generate unique invoice number based on period and company ID
    const periodStr = period.replace('/', '');
    const companySuffix = (company.id || '').substring(0, 6).toUpperCase();
    this.invoiceNumber = `INV-${periodStr}-${companySuffix}`;

    // Update selectedCompany with invoice number for template binding
    if (this.selectedCompany) {
      this.selectedCompany = {
        ...this.selectedCompany,
        invoiceNumber: this.invoiceNumber,
        statementPeriod: period
      };
    }

    this.isLoadingInvoice = false;
  }

  async getExchangeRate(currency: string, period: string): Promise<{ rate: number, date?: Date }> {
    const key = `${currency}_${period}`;
    if (this.exchangeRateCache.has(key)) {
      return this.exchangeRateCache.get(key)!;
    }

    const rateData = await this.invoiceService.getExchangeRate(currency, period);
    if (rateData !== null) {
      this.exchangeRateCache.set(key, rateData);
      return rateData;
    }

    try {
      // Fallback: Fetch latest rate from public API via service
      const apiData = await this.exchangeRateService.getCADtoUSDRate(period);
      if (apiData) {
        const data = { rate: apiData.rate, date: apiData.date };
        this.exchangeRateCache.set(key, data);
        return data;
      }

    } catch (error) {
      console.warn('Error fetching exchange rate:', error);
    }
    const defaultData = { rate: 1 };
    this.exchangeRateCache.set(key, defaultData);
    return defaultData; // Default fallback
  }

  viewInvoice(row: any) {
    this.selectedCompany = row.company;
    this.currentInvoicePeriod = row.period;
    this.isInvoiceModalOpen = true;
    this.isLoadingInvoice = true;

    setTimeout(async () => {
      await this.generateInvoiceData(row.company, row.period);
      this.isLoadingInvoice = false;
      this.cd.detectChanges();
    }, 100);
  }

  closeInvoiceModal() {
    this.isInvoiceModalOpen = false;
    this.selectedCompany = null;
    this.isLoadingInvoice = false;
  }

  private async handlePdfAction(company: any, action: 'save' | 'open' | 'blob', period: string): Promise<Blob | void> {
    let pdfWindow: Window | null = null;
    if (action === 'open') {
      // Open window immediately to avoid popup blockers
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    }

    this.selectedCompany = company;
    await this.generateInvoiceData(company, period);

    this.cd.detectChanges();
    // Wait for view to update
    await new Promise(resolve => setTimeout(resolve, 500));

    const content = document.getElementById('pdf-content');
    if (content) {
      const filename = `Invoice_${company.company.replace(/\s+/g, '_')}_${period.replace('/', '-')}.pdf`;

      try {
        if (action === 'save') {
          await this.pdfService.savePdf(content, filename, 'portrait');
        } else if (action === 'open') {
          await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow);
        } else if (action === 'blob') {
          return await this.pdfService.generateBlob(content, filename);
        }
      } catch (error) {
        if (pdfWindow) pdfWindow.close();
        throw error;
      }
    } else {
      console.error('PDF content element not found');
      if (pdfWindow) pdfWindow.close();
    }
  }

  async printInvoice(company: any) {
    // For print from modal, we use the already generated data in the modal
    // The modal is populated by viewInvoice which sets selectedCompany and generates data
    // So we just need to trigger the open action.
    // However, handlePdfAction regenerates data.
    // We can pass the period from the invoiceNumber or just use the current invoiceItems if we refactor.
    // But to keep it simple, we need the period.
    // The modal doesn't store the period explicitly other than in invoiceNumber or selectedCompany?
    // Actually, viewInvoice sets selectedCompany.
    // Let's extract period from invoiceNumber or store it.
    // Easier: store currentPeriod in component when generating invoice data.
    // But wait, printInvoice is called from the modal button.
    // The modal is open, so generateInvoiceData was called.
    // We can just use the period that was used.
    // Let's add `currentInvoicePeriod` property.
    if (this.currentInvoicePeriod) {
      await this.handlePdfAction(company, 'open', this.currentInvoicePeriod);
    }
  }

  currentInvoicePeriod: string = ''; // To track period for modal actions

  calculateTotals(beginningBalance: number = 0) {
    this.invoiceTotals = this.invoiceItems.reduce((acc, item) => {
      for (const [rateId, amount] of Object.entries(item.revenues)) {
        acc.revenues[rateId] = (acc.revenues[rateId] || 0) + (Number(amount) || 0);
      }
      return acc;
    }, {
      revenues: {}, fees: {},
      grandTotalRevenue: 0,
      totalFee: 0, beginningBalance: 0, totalDue: 0,
      isInternational: false, country: 'USA', currency: 'USD', exchangeRate: 1, totalDueUSD: 0
    });

    for (const rate of this.currentViewRates) {
      if (rate.id) {
        const rev = this.invoiceTotals.revenues[rate.id] || 0;
        const fee = this.selectedCompany?.isFeeCollection ? 0 : (rev * (rate.rate / 100));
        this.invoiceTotals.fees[rate.id] = fee;
        this.invoiceTotals.grandTotalRevenue += rev;
        this.invoiceTotals.totalFee += fee;
      }
    }

    this.invoiceTotals.beginningBalance = this.selectedCompany?.isFeeCollection ? 0 : beginningBalance;
    this.invoiceTotals.totalDue = this.invoiceTotals.beginningBalance + this.invoiceTotals.totalFee;
  }

  applyFilters(preservePage: boolean = false) {
    this.filteredGridData = this.gridData.filter(row => {
      const statusMatch = !this.filterStatus || row.status === this.filterStatus;
      const emailMatch = !this.filterEmailStatus || row.emailStatus === this.filterEmailStatus;
      const companyMatch = !this.filterCompany || (row.company.company || '').toLowerCase().includes(this.filterCompany.toLowerCase());
      const countryMatch = this.filterCountry === 'All' || row.country === this.filterCountry;
      const idMatch = this.filterCompanyIds.has(row.company.id);
      return statusMatch && emailMatch && companyMatch && countryMatch && idMatch;
    });

    if (!preservePage) {
      this.currentPage = 1;
    } else {
      const totalPages = Math.ceil(this.filteredGridData.length / this.pageSize);
      if (this.currentPage > totalPages) {
        this.currentPage = Math.max(1, totalPages);
      }
    }
    this.calculateDashboardStats();
  }

  get isAllSelected(): boolean {
    return this.filteredGridData.length > 0 && this.filteredGridData.every(r => this.selectedRowIds.has(r.id));
  }

  toggleSelectAll(event: any) {
    if (event.target.checked) {
      this.filteredGridData.forEach(row => this.selectedRowIds.add(row.id));
    } else {
      this.selectedRowIds.clear();
    }
  }

  toggleSelection(rowId: string) {
    if (this.selectedRowIds.has(rowId)) {
      this.selectedRowIds.delete(rowId);
    } else {
      this.selectedRowIds.add(rowId);
    }
  }

  get paginatedGridData(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredGridData.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredGridData.length / this.pageSize);
  }

  get paginationRange(): string {
    if (this.filteredGridData.length === 0) return '0 of 0';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.filteredGridData.length);
    return `${start} - ${end} of ${this.filteredGridData.length}`;
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  onPageSizeChange() {
    this.currentPage = 1;
  }

  async emailSingleInvoice(row: any) {
    this.currentEmailRow = row;
    this.isSavingPDFs = true;
    try {
      // 1. Generate PDF Blob using shared method
      const pdfBlob = await this.handlePdfAction(row.company, 'blob', row.period) as Blob;
      if (!pdfBlob) throw new Error('Failed to generate PDF blob');

      // 2. Upload to Firebase Storage
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const filename = `users/${user.uid}/temp_invoices/${Date.now()}_${(row.company.company || 'Company').replace(/\s+/g, '_')}.pdf`;
      const storageRef = ref(this.storage, filename);
      const metadata = { contentType: 'application/pdf' };
      await uploadBytes(storageRef, pdfBlob, metadata);
      const downloadURL = await getDownloadURL(storageRef);

      // 4. Open Email Modal with Attachment
      const email = row.company.email || '';
      const subject = `Invoice ${this.invoiceNumber} - ${row.company.company || 'Company'} - ${row.period}`;
      let body = `Dear ${row.company.pocName || 'Valued Partner'},\n\nI hope this email finds you well.\n\nPlease find attached the invoice for the billing period of ${row.period}.\n\nShould you have any questions or require further clarification regarding this invoice, please do not hesitate to contact us.\n\nThank you for your continued business.\n\nBest regards,\n\nAccounting Department\nKCEG Inc.`;

      if (user) {
        try {
          const template = await this.accessService.getEmailTemplate(user.uid);
          if (template) body = template;
        } catch (e) {
          console.error('Error loading email template:', e);
        }
      }

      this.emailData = {
        to: email,
        subject: subject,
        body: body,
        attachments: [{
          name: `Invoice_${row.company.company || 'Company'}_${row.period.replace('/', '-')}.pdf`,
          url: downloadURL,
          storagePath: filename // Used by backend to delete file
        }]
      };
      setTimeout(() => {
        this.isEmailModalOpen = true;
      });
    } catch (error) {
      console.error('Error generating invoice for email:', error);
      alert('Failed to generate invoice attachment.');
    } finally {
      this.isSavingPDFs = false;
    }
  }

  // --- Manual Invoice Add/Edit Logic ---

  openAddInvoiceModal() {
    this.isManualEditMode = false;
    this.manualNewStoreId = '';
    this.manualBeginningBalance = 0;
    this.isLoadingManualBalance = false;
    const initialPeriod = (this.selectedPeriod === 'All Periods' ? '' : this.selectedPeriod) || '';
    this.manualInvoiceData = {
      companyId: '',
      period: initialPeriod,
      items: [],
      adjustments: []
    };
    this.manualRates = this.getRatesForPeriod(initialPeriod);

    if (initialPeriod) {
      this.updateExistingCompaniesForManual(initialPeriod);
    } else {
      this.existingCompanyIdsForManualPeriod.clear();
    }

    this.isManualModalOpen = true;
  }

  async updateExistingCompaniesForManual(period: string) {
    this.existingCompanyIdsForManualPeriod.clear();
    if (!period) return;

    try {
      const aggSnap = await this.invoiceService.getAggregations(period);

      const storeIds = new Set<string>();
      aggSnap.forEach((d: any) => {
        storeIds.add(String(d['storeId']).trim());
      });

      this.stores.forEach(s => {
        const sid = String(s['storeId']).trim();
        if (storeIds.has(sid)) {
          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
          cids.forEach((cid: string) => this.existingCompanyIdsForManualPeriod.add(cid));
        }
      });

      const invSnap = await this.invoiceService.getInvoices(period);
      invSnap.forEach((d: any) => {
        const cid = d['companyId'];
        if (cid) this.existingCompanyIdsForManualPeriod.add(cid);
      });
    } catch (error) {
      console.error('Error checking existing companies:', error);
    }
  }

  onManualPeriodChange() {
    this.manualInvoiceData.companyId = '';
    this.manualInvoiceData.items = [];
    this.manualInvoiceData.adjustments = [];
    this.manualBeginningBalance = 0;
    this.manualRates = this.getRatesForPeriod(this.manualInvoiceData.period);
    this.updateExistingCompaniesForManual(this.manualInvoiceData.period);
  }

  get availableCompaniesForManual() {
    if (this.isManualEditMode) return this.activeTabCompanies;
    return this.activeTabCompanies.filter(c => !this.existingCompanyIdsForManualPeriod.has(c.id));
  }

  async openEditInvoiceModal(row: any) {
    this.isManualEditMode = true;
    this.manualNewStoreId = '';
    this.manualBeginningBalance = row.beginningBalance !== undefined ? row.beginningBalance : 0;
    this.isLoadingManualBalance = false;
    this.manualInvoiceData = {
      companyId: row.company.id,
      period: row.period,
      items: [],
      adjustments: []
    };
    this.manualRates = this.getRatesForPeriod(row.period);

    // 1. Identify stores linked to this company
    const companyId = row.company.id;
    const companyStoreIds = new Set(this.stores
      .filter(s => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId);
      })
      .map(s => String(s['storeId']).trim())
    );

    // Add stores from aggregations that belong to this company (custom stores)
    const snapshot = this.cachedAggregations.filter(d => d['period'] === row.period);
    snapshot.forEach(d => {
      if (d['companyId'] === companyId && d['storeId'] && !d['isAdjustment']) {
        companyStoreIds.add(String(d['storeId']).trim());
      }
    });

    // 2. Fetch existing aggregations for this period

    const storeAggMap = new Map<string, any>();
    snapshot.forEach((d: any) => {
      const sid = String(d['storeId'] || '').trim();
      // Only include stores relevant to this company (or if they have data), exclude adjustments here
      if (companyStoreIds.has(sid) && !d['isAdjustment']) {
        const current = storeAggMap.get(sid) || { revenues: {} };
        if (d['revenues']) {
          this.manualRates.forEach(r => {
            if (r.id) {
              const val = this.calculateRevenueForRate(d, r);
              current.revenues[r.id] = (current.revenues[r.id] || 0) + val;
            }
          });
        }
        storeAggMap.set(sid, current);
      }
    });

    // 3. Populate items list (include all linked stores, even if 0 revenue)
    const items: any[] = [];
    companyStoreIds.forEach(sid => {
      const data = storeAggMap.get(sid) || { revenues: {} };
      items.push({
        storeId: sid,
        revenues: data.revenues
      });
    });

    // 4. Populate adjustments
    const adjustments: any[] = [];
    snapshot.forEach((d: any) => {
      if (d['isAdjustment'] && d['companyId'] === companyId) {
        adjustments.push({
          name: d['storeId'],
          amounts: { ...d['revenues'] }
        });
      }
    });

    this.manualInvoiceData.items = items;
    this.manualInvoiceData.adjustments = adjustments;
    this.isManualModalOpen = true;

    // In edit mode, if beginningBalance wasn't in row, fetch it from previous month's remaining balance
    if (row.beginningBalance === undefined) {
      this.isLoadingManualBalance = true;
      try {
        this.manualBeginningBalance = await this.getBeginningBalance(companyId, row.period);
      } finally {
        this.isLoadingManualBalance = false;
      }
    }
  }

  async onManualCompanyChange() {
    if (this.isManualEditMode) return;

    const companyId = this.manualInvoiceData.companyId;
    if (!companyId) {
      this.manualInvoiceData.items = [];
      this.manualBeginningBalance = 0;
      return;
    }

    // Prepopulate with current relationship
    const relatedStores = this.stores.filter(s => {
      if (s.isDeleted) return false;
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      return cids.includes(companyId);
    });

    this.manualInvoiceData.items = relatedStores.map(s => ({
      storeId: s['storeId'],
      revenues: {}
    }));

    // Fetch beginning balance from previous month's remaining balance
    if (this.manualInvoiceData.period) {
      this.isLoadingManualBalance = true;
      try {
        this.manualBeginningBalance = await this.getBeginningBalance(companyId, this.manualInvoiceData.period);
      } catch (err) {
        console.error('Error fetching beginning balance for manual invoice:', err);
        this.manualBeginningBalance = 0;
      } finally {
        this.isLoadingManualBalance = false;
      }
    }
  }

  get availableStoresForManual() {
    const currentIds = new Set(this.manualInvoiceData.items.map(i => i.storeId));
    return this.stores
      .filter(s => !s.isDeleted && !currentIds.has(s['storeId']))
      .sort((a, b) => (a['storeId'] || '').toString().localeCompare((b['storeId'] || '').toString()));
  }

  addManualStore() {
    if (!this.manualNewStoreId) return;

    const isCustomAdd = this.manualNewStoreId === 'Custom';

    if (isCustomAdd) {
      this.manualInvoiceData.items.push({
        storeId: '',
        revenues: {},
        isCustom: true
      });
    } else {
      this.manualInvoiceData.items.push({
        storeId: this.manualNewStoreId,
        revenues: {}
      });
    }
    this.manualNewStoreId = '';

    if (isCustomAdd) {
      setTimeout(() => {
        const element = this.manualInvoiceBody?.nativeElement;
        if (element) element.scrollTop = element.scrollHeight;
      });
    }
  }

  removeManualStore(index: number) {
    this.manualInvoiceData.items.splice(index, 1);
  }

  addManualAdjustment() {
    this.manualInvoiceData.adjustments.push({ name: '', amounts: {} });
  }

  removeManualAdjustment(index: number) {
    this.manualInvoiceData.adjustments.splice(index, 1);
  }

  closeManualModal() {
    this.isManualModalOpen = false;
    this.manualBeginningBalance = 0;
    this.isLoadingManualBalance = false;
    this.manualInvoiceData = { companyId: '', period: '', items: [], adjustments: [] };
  }

  async saveManualInvoice() {
    if (!this.manualInvoiceData.companyId || !this.manualInvoiceData.period) return;
    if (this.manualRates.length === 0) return;

    this.isLoadingGrid = true;

    // Validation
    for (const item of this.manualInvoiceData.items) {
      if (item.isCustom) {
        if (!item.storeId || !item.storeId.trim()) {
          alert('Custom store name cannot be empty.');
          this.isLoadingGrid = false;
          return;
        }
      }
    }

    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'ManualEntry';
    const period = this.manualInvoiceData.period;
    const companyId = this.manualInvoiceData.companyId;
    const rates = this.getRatesForPeriod(period);

    try {
      // 1. Identify relevant stores (current company stores + any in the manual form)
      const companyStores = this.stores.filter(s => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId);
      });

      // Always resolve beginning balance from previous month's remaining balance
      const beginningBalance = await this.getBeginningBalance(companyId, period);
      await this.invoiceService.saveManualInvoice(this.manualInvoiceData, rates, userName, companyStores, beginningBalance);

      this.closeManualModal();
      this.gridDataCache.clear();
      this.rawDataCache.clear();
      await this.loadGridData();

      if (this.expandedRowId) {
        const row = this.gridData.find(r => r.id === this.expandedRowId);
        if (row) {
          await this.fetchCompanyDetails(row);
        }
      }
    } catch (err) {
      console.error('Error saving manual invoice:', err);
      alert('Failed to save invoice.');
    } finally {
      this.isLoadingGrid = false;
    }
  }

  async generateInvoicesForSelectedBrand() {
    if (!this.selectedPeriod || this.selectedPeriod === 'All Periods') {
      alert('Please select a specific billing period first.');
      return;
    }

    const rates = this.getRatesForPeriod(this.selectedPeriod);
    if (rates.length === 0) {
      alert(`No SMRS rates configured for ${this.activeBrandName} in period ${this.selectedPeriod}. Please set rates before generating invoices.`);
      return;
    }

    const brandCompanies = this.activeTabCompanies;
    if (brandCompanies.length === 0) {
      alert(`No companies found for brand ${this.activeBrandName}.`);
      return;
    }

    this.generationResult = null;
    this.isGenerationPreviewOpen = true;
    await this.refreshGenerationPreview();
  }

  /** Builds the read-only Generate preview; nothing is written (KAN-284). */
  async refreshGenerationPreview() {
    const period = this.selectedPeriod;
    if (!period) return;
    this.isPlanningGeneration = true;
    this.isGenerationStale = false;
    this.generationPlanError = '';
    this.generationPlan = null;
    this.expandedGenerationRows.clear();
    this.cd.detectChanges();
    try {
      this.generationPlan = await this.invoiceService.planBrandGeneration(
        period,
        this.activeBrandName,
        this.activeTabCompanies,
        this.getRatesForPeriod(period),
        this.stores,
        this.photoboothService,
        this.storeService,
        this.activeBrandTab
      );
    } catch (err) {
      console.error('Error building generation preview:', err);
      this.generationPlanError = 'Failed to build the preview. Please try again.';
    } finally {
      this.isPlanningGeneration = false;
      this.cd.detectChanges();
    }
  }

  cancelGenerationPreview() {
    if (this.isGeneratingInvoices) return;
    this.isGenerationPreviewOpen = false;
    this.generationPlan = null;
    this.isGenerationStale = false;
    this.generationPlanError = '';
  }

  async confirmGeneration() {
    if (!this.generationPlan || this.isGeneratingInvoices || this.isGenerationStale) return;

    this.isGeneratingInvoices = true;
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'AutoGenerate';

    try {
      const result = await this.invoiceService.applyBrandGeneration(
        this.generationPlan,
        this.activeTabCompanies,
        this.getRatesForPeriod(this.generationPlan.period),
        userName,
        this.stores,
        this.photoboothService,
        this.storeService
      );

      this.isGenerationPreviewOpen = false;
      this.generationPlan = null;
      this.generationResult = result;

      // Refresh in place: filters, selection and the expanded row are kept.
      this.gridDataCache.clear();
      this.rawDataCache.clear();
      this.fileProcessedCache.clear();
      await this.loadGridData();
      if (this.expandedRowId) {
        const row = this.gridData.find(r => r.id === this.expandedRowId);
        if (row) await this.fetchCompanyDetails(row);
      }
    } catch (err) {
      if (err instanceof StaleGenerationPlanError) {
        this.isGenerationStale = true;
      } else {
        console.error('Error generating invoices for brand:', err);
        this.generationPlanError = 'Failed to generate SMRS invoices. Please try again.';
      }
    } finally {
      this.isGeneratingInvoices = false;
      this.cd.detectChanges();
    }
  }

  closeGenerationResult() {
    this.generationResult = null;
  }

  toggleGenerationRow(companyId: string) {
    if (this.expandedGenerationRows.has(companyId)) {
      this.expandedGenerationRows.delete(companyId);
    } else {
      this.expandedGenerationRows.add(companyId);
    }
  }

  get canConfirmGeneration(): boolean {
    return !!this.generationPlan && !this.isPlanningGeneration && !this.isGeneratingInvoices && !this.isGenerationStale
      && this.generationPlan.companies.some(c => this.isGenerationWritable(c));
  }

  /** The review lists only companies that will change; kept and unchanged ones are counted, not listed. */
  get changingGenerationCompanies(): GenerationCompanyPlan[] {
    return (this.generationPlan?.companies || []).filter(c => this.isGenerationWritable(c));
  }

  get unchangedGenerationCount(): number {
    return (this.generationPlan?.companies.length || 0) - this.changingGenerationCompanies.length;
  }

  isGenerationWritable(c: GenerationCompanyPlan): boolean {
    return willGenerate(c);
  }

  /** Companies the run skipped (kept or unchanged); the results list only the regenerated ones. */
  get skippedGenerationCount(): number {
    return this.generationResult ? this.generationResult.counts.kept + this.generationResult.counts.unchanged : 0;
  }

  generationLabel(c: GenerationCompanyPlan): string {
    const base = c.classification === 'new' ? 'New' : 'Refresh';
    return c.missingDevices.length ? `${base} · Partial` : base;
  }

  generationOutcomeLabel(outcome: string): string {
    switch (outcome) {
      case 'created': return 'Created';
      case 'refreshed': return 'Refreshed';
      case 'unchanged': return 'Unchanged';
      case 'kept-manual': return 'Kept – Manual';
      case 'kept-canceled': return 'Kept – Canceled';
      default: return 'Failed';
    }
  }

  async onEmailSent(event?: any) {
    if (event?.to) {
      this.emailData.to = event.to;
    }

    if (this.currentEmailRow) {
      const attachmentUrl = this.emailData.attachments?.[0]?.url || null;
      const user = this.auth.currentUser;
      const userName = user?.displayName || user?.email || 'Admin';
      await this.updateEmailStatus(this.currentEmailRow, attachmentUrl, userName);
    }
    this.closeEmailModal(false);
  }

  async updateEmailStatus(row: any, attachmentUrl: string | null, userName: string) {
    try {
      const now = new Date();
      const recipients = this.emailData.to;
      await this.invoiceService.updateEmailStatus(row, recipients, attachmentUrl, userName);

      row.emailStatus = 'Sent';
      row.emailSentAt = now;
      if (recipients) row.emailRecipients = recipients;
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error updating email status:', error);
    }
  }

  private propagateBalanceUpdateLocally(companyId: string, currentPeriod: string, delta: number) {
    let blockingPeriod: string | null = null;
    const futureRowsWithOverride = this.gridData
      .filter(r => r.company.id === companyId && r.period > currentPeriod && r.beginningBalanceOverride !== undefined);

    if (futureRowsWithOverride.length > 0) {
      futureRowsWithOverride.sort((a, b) => a.period.localeCompare(b.period));
      blockingPeriod = futureRowsWithOverride[0].period;
    }

    this.gridData.forEach(row => {
      if (row.company.id === companyId && row.period > currentPeriod) {
        if (blockingPeriod && row.period >= blockingPeriod) return;
        if (row.status === 'Canceled') return;

        row.beginningBalance += delta;
        row.totalDue += delta;
        row.remainingBalance += delta;

        if (row.status !== 'Canceled') {
          if (row.remainingBalance <= 0.01) {
            row.status = 'Paid';
          } else if (row.amountPaid > 0.01) {
            row.status = 'Partial';
          } else {
            row.status = 'Not Paid';
          }
        }
        this.updateRowUSDValues(row);
        if (this.expandedRowId === row.id && this.expandedCompanyTotals) {
          this.expandedCompanyTotals.remainingBalanceUSD = row.remainingBalanceUSD;
        }
      }
    });
  }

  private updateRowUSDValues(row: any) {
    const exchangeRate = row.exchangeRate || 1;
    row.totalRevenueUSD = (row.totalRevenue || 0) * exchangeRate;
    row.totalFeeUSD = (row.totalFee || 0) * exchangeRate;
    row.totalDueUSD = (row.totalDue || 0) * exchangeRate;
    row.remainingBalanceUSD = (row.remainingBalance || 0) * exchangeRate;
    row.amountPaidUSD = (row.amountPaid || 0) * exchangeRate;
  }

  async deleteSelectedInvoices() {
    if (this.selectedRowIds.size === 0) return;

    const selectedRows = this.gridData.filter(r => this.selectedRowIds.has(r.id) && !r.company?.isFeeCollection);
    if (selectedRows.length === 0) return;

    // Validate future records for all selected rows
    for (const row of selectedRows) {
      try {
        const invoices = await this.invoiceService.getInvoices(undefined, row.company.id);
        const hasFuture = invoices.some((inv: any) => inv.period > row.period);

        if (hasFuture) {
          alert(`Unable to delete invoice for "${row.company.company}" (${row.period}) because invoice data exists for later periods. To maintain ledger integrity, please delete invoices starting from the latest period.`);
          return;
        }
      } catch (error) {
        console.error('Error validating invoice deletion for company:', row.company.company, error);
        return;
      }
    }

    const companyNames = selectedRows.map(r => r.company.company).join(', ');
    if (!confirm(`Are you sure you want to delete ${selectedRows.length} selected invoice(s)?\n\nCompanies: ${companyNames}\n\nThis action cannot be undone.`)) {
      return;
    }

    this.isLoadingGrid = true;
    try {
      for (const row of selectedRows) {
        const companyId = row.company.id;
        const companyStores = this.stores.filter(s => {
          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
          return cids.includes(companyId);
        });
        const storeIds = companyStores.map(s => String(s['storeId']).trim());

        const delta = await this.invoiceService.deleteInvoice(row, storeIds);

        this.gridData = this.gridData.filter(r => r.id !== row.id);
        if (Math.abs(delta) > 0.001) {
          this.propagateBalanceUpdateLocally(companyId, row.period, delta);
        }
      }

      this.selectedRowIds.clear();
      this.gridDataCache.clear();
      await this.recalculateLocks();
      this.applyFilters();
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error deleting selected invoices:', error);
      alert('Failed to delete some or all selected invoices.');
    } finally {
      this.isLoadingGrid = false;
      this.cd.detectChanges();
    }
  }

  async deleteInvoice(row: any) {
    try {
      const invoices = await this.invoiceService.getInvoices(undefined, row.company.id);
      const hasFuture = invoices.some((inv: any) => inv.period > row.period);

      if (hasFuture) {
        alert(`Unable to delete invoice for "${row.company.company}" (${row.period}) because invoice data exists for later periods. To maintain ledger integrity, please delete invoices starting from the latest period.`);
        return;
      }
    } catch (error) {
      console.error('Error validating invoice deletion:', error);
      return;
    }

    if (!confirm(`Are you sure you want to delete the invoice for ${row.company.company} (${row.period})? This action cannot be undone.`)) {
      return;
    }

    try {
      const companyId = row.company.id;
      const companyStores = this.stores.filter(s => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId);
      });
      const storeIds = companyStores.map(s => String(s['storeId']).trim());

      const delta = await this.invoiceService.deleteInvoice(row, storeIds);

      this.gridData = this.gridData.filter(r => r.id !== row.id);
      this.selectedRowIds.delete(row.id);
      this.gridDataCache.clear();
      if (Math.abs(delta) > 0.001) {
        this.propagateBalanceUpdateLocally(companyId, row.period, delta);
      }
      await this.recalculateLocks();
      this.applyFilters();
      this.cd.detectChanges();

    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice.');
    }
  }

  startEditingBeginningBalance() {
    if (this.expandedCompanyTotals) {
      this.expandedCompanyTotals.isEditingBeginningBalance = true;
      this.expandedCompanyTotals.newBeginningBalance = this.expandedCompanyTotals.beginningBalance;
    }
  }

  cancelEditingBeginningBalance() {
    if (this.expandedCompanyTotals) {
      this.expandedCompanyTotals.isEditingBeginningBalance = false;
    }
  }

  async saveBeginningBalance() {
    if (!this.expandedRowId || !this.expandedCompanyTotals) return;
    const row = this.gridData.find(r => r.id === this.expandedRowId);
    if (!row) return;

    const newBalance = this.expandedCompanyTotals.newBeginningBalance;
    const currentBalance = this.expandedCompanyTotals.beginningBalance;

    if (newBalance === currentBalance) {
      this.cancelEditingBeginningBalance();
      return;
    }

    const delta = newBalance - currentBalance;
    const currentPeriod = row.period;
    const companyId = row.company.id;

    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';

    try {
      const totalFee = this.expandedCompanyTotals.totalFee || 0;
      await this.invoiceService.saveBeginningBalance(row, newBalance, totalFee, row.amountPaid || 0, userName);

      const amountPaid = row.amountPaid || 0;
      const newTotalDue = Math.round((newBalance + totalFee) * 100) / 100;
      const newRemaining = Math.round((newTotalDue - amountPaid) * 100) / 100;
      let newStatus = 'Not Paid';
      if (newRemaining <= 0.01) newStatus = 'Paid';
      else if (amountPaid > 0.01) newStatus = 'Partial';

      // Update local row data immediately
      row.beginningBalanceOverride = newBalance;
      row.remainingBalance = newRemaining;
      row.totalDue = newTotalDue;
      row.status = newStatus;
      row.updatedAt = new Date();
      row.updatedBy = userName;

      this.updateRowUSDValues(row);
      this.gridDataCache.clear();
      this.rawDataCache.clear();
      await this.loadGridData(true);

      if (this.expandedRowId) {
        const newRow = this.gridData.find(r => r.id === this.expandedRowId);
        if (newRow) {
          await this.fetchCompanyDetails(newRow);
        }
      }

      await this.recalculateLocks();
      this.applyFilters(true);
      this.cd.detectChanges();

    } catch (error) {
      console.error('Error saving beginning balance:', error);
      alert('Failed to update beginning balance.');
    }
  }

  isDistributionModalOpen = false;
  distributionData: any = null;
  loadingDistributionId: string | null = null;
  updatingPartialId: string | null = null;
  isConfirmingDistribution: boolean = false;

  async updateInvoiceStatus(row: any, newStatus: string) {
    if (row.status === 'Canceled') return;

    row.isUpdatingStatus = true;

    try {
      let newAmountPaid = row.displayAmountPaid !== undefined ? row.displayAmountPaid : row.amountPaid;
      if (newStatus === 'Paid') {
        newAmountPaid = row.totalDue;
      } else if (newStatus === 'Not Paid') {
        newAmountPaid = 0;
      } else if (newStatus === 'Partial') {
        row.status = 'Partial';
        return;
      }

      await this.checkAndShowDistribution(row, newAmountPaid, newStatus);
    } finally {
      row.isUpdatingStatus = false;
    }
  }

  async updateAmountPaid(row: any) {
    this.updatingPartialId = row.id;
    try {
      let newAmountPaid = Number(row.displayAmountPaid) || 0;

      let newStatus = row.status;
      if (newAmountPaid >= row.totalDue - 0.01) {
        newStatus = 'Paid';
      } else if (newAmountPaid <= 0.01) {
        newStatus = 'Not Paid';
      } else {
        newStatus = 'Partial';
      }

      await this.checkAndShowDistribution(row, newAmountPaid, newStatus);
    } finally {
      this.updatingPartialId = null;
    }
  }

  async checkAndShowDistribution(row: any, newAmountPaid: number, newStatus: string) {
    const beginningBalance = row.expandedCompanyTotals ? row.expandedCompanyTotals.beginningBalance : (row.beginningBalance || 0);

    if (beginningBalance > 0.01 && newAmountPaid > 0.01) {
      try {
        const dist = await this.calculateFifoDistribution(row.company, row.period, newAmountPaid);
        const affectsOtherPeriods = dist.changes.some((c: any) => c.period !== row.period);

        if (affectsOtherPeriods) {
          this.distributionData = {
            row,
            newAmountPaid,
            newStatus,
            periodsData: dist.periodsData,
            changes: dist.changes
          };
          this.isDistributionModalOpen = true;
          return;
        }
      } catch (e) {
        console.error('Error calculating payment distribution', e);
      }
    }

    try {
      row.actualAmountPaid = newAmountPaid;
      row.displayAmountPaid = newAmountPaid;
      row.amountPaid = newAmountPaid;
      row.remainingBalance = Math.round((row.totalDue - newAmountPaid) * 100) / 100;

      if (newStatus === 'Not Paid') {
        row.amountPaid = 0;
        row.remainingBalance = Math.round(row.totalDue * 100) / 100;
        const result = await this.invoiceService.updateInvoiceStatus(row, 'Not Paid', this.auth.currentUser?.displayName || 'Admin');
        await this.applySingleUpdateResult(row, 'Not Paid', 0, result);
      } else if (newStatus === 'Paid' && newAmountPaid <= row.totalDue) {
        // Standard Paid click or exact totalDue payment without overpayment
        row.amountPaid = Math.max(0, row.totalDue);
        row.remainingBalance = Math.round((row.totalDue - row.amountPaid) * 100) / 100;
        const result = await this.invoiceService.updateInvoiceStatus(row, 'Paid', this.auth.currentUser?.displayName || 'Admin');
        await this.applySingleUpdateResult(row, 'Paid', row.amountPaid, result);
      } else {
        // Overpayment (newAmountPaid > row.totalDue) or Partial payment (0 < newAmountPaid < row.totalDue)
        const result = await this.invoiceService.updateRemainingBalance(row, this.auth.currentUser?.displayName || 'Admin');
        await this.applySingleUpdateResult(row, newStatus, newAmountPaid, result);
      }
    } catch (e) {
      console.error('Error updating invoice status', e);
    }
  }

  async viewDistribution(row: any) {
    this.loadingDistributionId = row.id;
    try {
      const actualAmountPaid = row.displayAmountPaid !== undefined ? row.displayAmountPaid : (row.amountPaid || 0);
      const dist = await this.calculateFifoDistribution(row.company, row.period, actualAmountPaid);
      this.distributionData = {
        row,
        newAmountPaid: actualAmountPaid,
        newStatus: row.status,
        periodsData: dist.periodsData,
        changes: dist.changes,
        isReadOnly: true
      };
      this.isDistributionModalOpen = true;
    } catch (e) {
      console.error(e);
      alert('Error fetching distribution data.');
    } finally {
      this.loadingDistributionId = null;
    }
  }

  async applySingleUpdateResult(row: any, newStatus: string, newAmountPaid: number, result: any) {
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';

    // 1. Immediate optimistic visual update for responsive feel
    row.status = newStatus;
    row.amountPaid = newAmountPaid;
    row.remainingBalance = Math.round((row.totalDue - newAmountPaid) * 100) / 100;
    row.isEdited = true;
    row.updatedAt = new Date();
    row.updatedBy = userName;
    this.updateRowUSDValues(row);

    // 2. Clear caches and silently refresh verified database numbers without full page reload
    this.gridDataCache.clear();
    this.rawDataCache.clear();
    await this.loadGridData(true);

    if (this.expandedRowId) {
      const updatedRow = this.gridData.find(r => r.id === this.expandedRowId);
      if (updatedRow) {
        await this.fetchCompanyDetails(updatedRow);
      }
    }

    await this.recalculateLocks();
    this.applyFilters(true);
    this.cd.detectChanges();
  }

  cancelDistribution() {
    this.isDistributionModalOpen = false;
    if (this.distributionData && !this.distributionData.isReadOnly) {
      const row = this.distributionData.row;
      row.displayAmountPaid = row.actualAmountPaid !== undefined ? row.actualAmountPaid : row.amountPaid;
    }
    this.distributionData = null;
  }

  async confirmDistribution() {
    if (!this.distributionData) return;
    this.isConfirmingDistribution = true;
    try {
      const { row, newAmountPaid, newStatus, changes } = this.distributionData;
      await this.executeDistribution(row, newAmountPaid, newStatus, changes);
    } finally {
      this.isConfirmingDistribution = false;
      this.isDistributionModalOpen = false;
    }
  }

  async executeDistribution(row: any, newAmountPaid: number, newStatus: string, changes: any[]) {
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';

    try {
      // Prepare appliedFrom for each change before passing to service
      for (const change of changes) {
        const paidDelta = change.newPaid - change.oldPaid;
        if (change.period !== row.period && paidDelta !== 0) {
          let appliedFrom = change.originalInv?.appliedFrom ? JSON.parse(JSON.stringify(change.originalInv.appliedFrom)) : [];
          if (paidDelta > 0) {
            const existing = appliedFrom.find((x: any) => x.period === row.period);
            if (existing) existing.amount += paidDelta;
            else appliedFrom.push({ period: row.period, amount: paidDelta });
          } else {
            let rem = -paidDelta;
            for (let i = appliedFrom.length - 1; i >= 0; i--) {
              if (rem <= 0) break;
              if (appliedFrom[i].period === row.period) {
                if (appliedFrom[i].amount > rem) { appliedFrom[i].amount -= rem; rem = 0; }
                else { rem -= appliedFrom[i].amount; appliedFrom.splice(i, 1); }
              }
            }
            for (let i = appliedFrom.length - 1; i >= 0; i--) {
              if (rem <= 0) break;
              if (appliedFrom[i].amount > rem) { appliedFrom[i].amount -= rem; rem = 0; }
              else { rem -= appliedFrom[i].amount; appliedFrom.splice(i, 1); }
            }
          }
          change.appliedFrom = appliedFrom.filter((x: any) => x.amount > 0.01);
        }
      }

      await this.invoiceService.applyPaymentDistribution(row.company, changes, userName, row.period, newAmountPaid);

      const currentRow = this.gridData.find(r => r.company.id === row.company.id && r.period === row.period);
      if (currentRow) {
        currentRow.actualAmountPaid = newAmountPaid;
        currentRow.displayAmountPaid = newAmountPaid;
        currentRow.isEdited = true;
        currentRow.updatedAt = new Date();
        currentRow.updatedBy = userName;
      }

      let lastDelta = 0;
      let lastPeriod = '';

      for (const change of changes) {
        const rowToUpdate = this.gridData.find(r => r.company.id === row.company.id && r.period === change.period);
        if (rowToUpdate) {
          lastDelta = change.newRem - rowToUpdate.remainingBalance;
          lastPeriod = change.period;

          rowToUpdate.amountPaid = change.newPaid;
          rowToUpdate.remainingBalance = change.newRem;
          rowToUpdate.totalDue = change.totalDue;
          rowToUpdate.beginningBalance = change.begBal;
          if (change.newRem <= 0.01) rowToUpdate.status = 'Paid';
          else if (change.newPaid > 0.01) rowToUpdate.status = 'Partial';
          else rowToUpdate.status = 'Not Paid';

          rowToUpdate.isEdited = true;
          rowToUpdate.updatedAt = new Date();
          rowToUpdate.updatedBy = userName;

          if (change.appliedFrom) {
            rowToUpdate.appliedFrom = change.appliedFrom;
          }

          this.updateRowUSDValues(rowToUpdate);
          if (this.expandedRowId === rowToUpdate.id && this.expandedCompanyTotals) {
            this.expandedCompanyTotals.remainingBalanceUSD = rowToUpdate.remainingBalanceUSD;
          }
        }

        const paidDelta = change.newPaid - change.oldPaid;
        if (paidDelta !== 0) {
          const feeCollectionCompany = this.companies.find(c => c.brand === row.company.brand && c.isFeeCollection);
          if (feeCollectionCompany && feeCollectionCompany.id !== row.company.id) {
            const exchangeRate = change.exchangeRate || 1;
            const transferredAmountUSD = paidDelta * exchangeRate;
            await this.updateFeeCollectionDataLocally(feeCollectionCompany.id, change.period, paidDelta, transferredAmountUSD, 0, 0, undefined, row.company.id);
          }
        }
      }

      this.gridDataCache.clear();
      this.rawDataCache.clear();
      await this.loadGridData(true);

      if (this.expandedRowId) {
        const newRow = this.gridData.find(r => r.id === row.id);
        if (newRow) {
          await this.fetchCompanyDetails(newRow);
        }
      }

      await this.recalculateLocks();
      this.applyFilters(true);
      this.cd.detectChanges();
    } catch (e) {
      console.error(e);
      alert('Failed to apply payment distribution.');
    }
  }

  async calculateFifoDistribution(company: any, currentPeriod: string, newAmountPaidForCurrentPeriod: number) {
    const companyId = company.id;
    const invoices = await this.invoiceService.getInvoices(undefined, companyId);
    invoices.sort((a: any, b: any) => a.period.localeCompare(b.period));

    const allPeriodsSet = new Set<string>();
    invoices.forEach(inv => allPeriodsSet.add(inv.period));
    allPeriodsSet.add(currentPeriod);
    if (this.cachedAggregations) {
      this.cachedAggregations.forEach(d => {
        if (d.companyId === companyId && d.period) {
          allPeriodsSet.add(d.period);
        }
      });
    }

    const chronologicalPeriods = Array.from(allPeriodsSet).sort();
    const relevantPeriods = [];
    for (const p of chronologicalPeriods) {
      relevantPeriods.push(p);
      if (p === currentPeriod) break;
    }

    const periodsData: any[] = [];
    let totalPool = 0;
    let runningRem = 0;

    for (const period of relevantPeriods) {
      const inv = invoices.find((i: any) => i.period === period);

      if (inv && inv.status === 'Canceled') {
        runningRem = 0;
        continue;
      }

      let begBal = runningRem;
      if (inv && inv.beginningBalanceOverride !== undefined) {
        begBal = inv.beginningBalanceOverride;
      }

      let totalLfcFee = 0;
      let totalKcegFee = 0;
      let exchangeRate = 1;

      if (inv && (inv.totalFee !== undefined && inv.totalFee !== null)) {
        const tf = Number(inv.totalFee);
        totalLfcFee = inv.totalLfcFee !== undefined ? Number(inv.totalLfcFee) : tf * 0.6;
        totalKcegFee = inv.totalKcegFee !== undefined ? Number(inv.totalKcegFee) : tf * 0.4;
      } else {
        const existingRow = this.gridData.find(r => r.id === `${period}_${companyId}`);
        if (existingRow && (existingRow.totalFee !== undefined || existingRow.totalLfcFee !== undefined)) {
          totalLfcFee = existingRow.totalLfcFee || 0;
          totalKcegFee = existingRow.totalKcegFee || 0;
          exchangeRate = existingRow.exchangeRate || 1;
        } else {
          const aggDocs = await this.invoiceService.getAggregations(period);
          const periodRates = this.getRatesForPeriod(period);
          const stores = this.stores.filter(s => (s['companyIds'] || []).includes(companyId) || s['companyId'] === companyId);

          aggDocs.forEach(d => {
            if (stores.some(s => s.storeId === String(d.storeId).trim()) || (d['companyId'] === companyId)) {
              periodRates.forEach(r => {
                const val = this.invoiceService.calculateRevenueForRate(d, r);
                const fee = val * (r.rate / 100);
                const kcegRateVal = Number((r as any).kcegRate) || 0;
                const lfcRateVal = (r.rate || 0) - kcegRateVal;
                if (r.rate) {
                  totalKcegFee += fee * (kcegRateVal / r.rate);
                  totalLfcFee += fee * (lfcRateVal / r.rate);
                }
              });
            }
          });

          const currency = this.companyCurrencyMap.get(companyId) || 'USD';
          if (currency !== 'USD') {
            const rateData = await this.getExchangeRate(currency, period);
            exchangeRate = rateData.rate;
          }
        }
      }

      const netCharge = company.isFeeCollection ? 0 : 
        (inv && inv.totalFee !== undefined && inv.totalFee !== null ? Number(inv.totalFee) : Math.round((totalLfcFee + totalKcegFee) * 100) / 100);
      const currentApplied = inv ? (inv.amountPaid || 0) : 0;
      const totalDue = Math.round((begBal + netCharge) * 100) / 100;

      let paymentToInclude = 0;
      if (period === currentPeriod) {
        paymentToInclude = newAmountPaidForCurrentPeriod;
      } else if (inv) {
        // Cash natively received in this period, derived from the ledger itself (never from the
        // `actualAmountPaid` snapshot, which can be stale after a revert/partial edit and inflate the pool):
        //   cash = amountPaid - funds received from other periods + funds this period pushed to earlier periods.
        const appliedIn = (inv.appliedFrom || []).reduce((sum: number, a: any) => sum + (a.amount || 0), 0);
        const pushedOut = invoices.reduce((sum: number, other: any) => sum + (other.appliedFrom || [])
          .filter((a: any) => a.period === period)
          .reduce((s: number, a: any) => s + (a.amount || 0), 0), 0);
        paymentToInclude = Math.max(0, Math.round(((inv.amountPaid || 0) - appliedIn + pushedOut) * 100) / 100);
      }

      totalPool += paymentToInclude;

      periodsData.push({
        id: inv ? inv.id : null,
        docId: inv ? (inv.docId || inv.id) : null,
        period: period,
        netCharge: netCharge,
        oldPaid: currentApplied,
        newPaid: 0,
        totalDue: totalDue,
        begBal: begBal,
        originalInv: inv || {},
        totalLfcFee,
        totalKcegFee,
        exchangeRate
      });

      runningRem = Math.round((totalDue - currentApplied) * 100) / 100;
    }

    let currentPool = totalPool;
    let newRunningRem = 0;

    for (let i = 0; i < periodsData.length; i++) {
      const p = periodsData[i];

      let begBal = (i === 0) ? 0 : newRunningRem;
      if (p.originalInv.beginningBalanceOverride !== undefined) {
        begBal = p.originalInv.beginningBalanceOverride;
      }

      const periodTotalDue = begBal + p.netCharge;
      p.totalDue = periodTotalDue;
      p.begBal = begBal;

      if (periodTotalDue <= 0) {
        p.newPaid = 0;
        newRunningRem = periodTotalDue;
        p.newRem = newRunningRem;
      } else if (currentPool >= periodTotalDue) {
        p.newPaid = periodTotalDue;
        currentPool -= periodTotalDue;
        newRunningRem = periodTotalDue - p.newPaid;
        p.newRem = newRunningRem;
      } else {
        p.newPaid = currentPool;
        currentPool = 0;
        newRunningRem = periodTotalDue - p.newPaid;
        p.newRem = newRunningRem;
      }
    }

    if (currentPool > 0 && periodsData.length > 0) {
      periodsData[periodsData.length - 1].newPaid += currentPool;
      periodsData[periodsData.length - 1].newRem -= currentPool;
      newRunningRem -= currentPool;
    }

    const changes = periodsData.filter(p => 
      Math.abs(p.newPaid - p.oldPaid) > 0.001 ||
      Math.abs(p.newRem - (p.originalInv.remainingBalance !== undefined ? p.originalInv.remainingBalance : p.totalDue)) > 0.001 ||
      Math.abs(p.begBal - (p.originalInv.beginningBalance !== undefined ? p.originalInv.beginningBalance : 0)) > 0.001
    );

    return { periodsData, changes };
  }

  async toggleCancelStatus(row: any) {
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';
    try {
      const result = await this.invoiceService.toggleCancelStatus(row, userName);
      if (!result) return;

      this.gridDataCache.clear();
      this.rawDataCache.clear();
      await this.loadGridData(true);

      if (this.expandedRowId) {
        const newRow = this.gridData.find(r => r.id === this.expandedRowId);
        if (newRow) {
          await this.fetchCompanyDetails(newRow);
        }
      }

      await this.recalculateLocks();
      this.applyFilters(true);
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error toggling cancel status:', error);
    }
  }

  private async updateFeeCollectionDataLocally(feeCollectionCompanyId: string, period: string, transferredAmount: number, transferredAmountUSD: number, feeDelta: number, feeDeltaUSD: number, newAggregation?: any, sourceCompanyId?: string) {
    const feeCollectionRow = this.gridData.find(r => r.company.id === feeCollectionCompanyId && r.period === period);

    if (!feeCollectionRow) {
      // If the fee collection company doesn't have a row for this period, we can't update it locally.
      // Fallback to reloading the grid to create the new row.
      console.warn(`Fee Collection company row not found for period ${period}. Reloading grid.`);
      this.rawDataCache.delete(period);
      await this.loadGridData();
      return;
    }

    // Update revenue
    feeCollectionRow.totalRevenue = (feeCollectionRow.totalRevenue || 0) + transferredAmount;
    feeCollectionRow.totalRevenueUSD = (feeCollectionRow.totalRevenueUSD || 0) + transferredAmountUSD;

    // Update fees
    feeCollectionRow.totalFee = 0;
    feeCollectionRow.totalFeeUSD = 0;

    // Update due and remaining balance
    feeCollectionRow.totalDue = 0;
    feeCollectionRow.totalDueUSD = 0;
    feeCollectionRow.remainingBalance = 0;
    feeCollectionRow.remainingBalanceUSD = 0;

    // Update status based on new balance
    if (feeCollectionRow.status !== 'Canceled') {
      feeCollectionRow.status = 'Paid';
    }

    if (sourceCompanyId) {
      this.cachedAggregations = this.cachedAggregations.filter(a => !(a.companyId === feeCollectionCompanyId && a.sourceCompanyId === sourceCompanyId && a.period === period));
      this.rawDataCache.set(period, this.cachedAggregations);
    }

    if (newAggregation) {
      this.cachedAggregations.push(newAggregation);
      this.rawDataCache.set(period, this.cachedAggregations);
    }

    if (this.expandedRowId === feeCollectionRow.id) {
      await this.fetchCompanyDetails(feeCollectionRow);
    }
  }

  async closeEmailModal(shouldDelete: boolean = false) {
    if (shouldDelete && this.emailData.attachments && this.emailData.attachments.length > 0) {
      for (const attachment of this.emailData.attachments) {
        if (attachment.storagePath) {
          try {
            const fileRef = ref(this.storage, attachment.storagePath);
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('Error deleting temp file:', error);
          }
        }
      }
    }
    this.isEmailModalOpen = false;
    this.emailData = { to: '', subject: '', body: '', attachments: [] };
    this.currentEmailRow = null;
  }

  openRecipientsModal(recipients: string) {
    if (!recipients) {
      this.currentRecipients = [];
    } else {
      this.currentRecipients = recipients.split(/[,;\s]+/).map(r => r.trim()).filter(r => r);
    }
    this.isRecipientsModalOpen = true;
  }

  closeRecipientsModal() {
    this.isRecipientsModalOpen = false;
    this.currentRecipients = [];
  }

  getRecipientSummary(recipients: string): { first: string, count: number } {
    if (!recipients) return { first: '', count: 0 };
    const list = recipients.split(/[,;\s]+/).map(r => r.trim()).filter(r => r);
    return {
      first: list[0] || '',
      count: Math.max(0, list.length - 1)
    };
  }

  async saveSelectedInvoices() {
    this.isSavingPDFs = true;
    const selectedRows = this.gridData.filter(r => this.selectedRowIds.has(r.id));

    try {
      for (const row of selectedRows) {
        await this.handlePdfAction(row.company, 'save', row.period);
      }
    } finally {
      this.isSavingPDFs = false;
    }
  }

  openNewStoresModal() {
    this.isNewStoresModalOpen = true;
  }

  closeNewStoresModal() {
    this.isNewStoresModalOpen = false;
  }

  openRateCustomization(period?: string) {
    let periodToOpen = period || this.selectedPeriod;
    if (periodToOpen === 'All Periods' || !periodToOpen) {
      periodToOpen = this.periods.length > 0 ? this.periods[0] : '';
    }
    if (this.rateCustomization) {
      if (this.selectedBrand) {
        this.rateCustomization.selectedBrandId = this.selectedBrand.id || '';
        this.rateCustomization.selectedBrandName = this.selectedBrand.name || 'LFC';
      }
      this.rateCustomization.open(periodToOpen);
    }
  }

  calculateIncentiveStats() {
    // Reset stats
    this.incentiveStats = {
      rateNames: [],
      lfc: {},
      kceg: {},
      kcegRates: {},
      lfcRates: {},
      total: {},
      grandTotalLfc: 0,
      grandTotalKceg: 0,
      grandTotal: 0
    };

    if (!this.cachedAggregations || this.cachedAggregations.length === 0) return;

    // Build a set of currently visible row keys (period_companyId) to only compute stats for visible data
    const visibleRowKeys = new Set(
      this.filteredGridData
        .filter(r => !r.company?.isFeeCollection)
        .map(r => r.id)
    );

    // Helper to map store to companies
    const storeToCompanyMap = new Map<string, string[]>();
    this.stores.forEach(s => {
      const sid = String(s['storeId'] || '').trim();
      if (sid) {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        storeToCompanyMap.set(sid, cids);
      }
    });

    // Group rates by period for fast lookup
    const ratesByPeriod = new Map<string, SmrsRate[]>();
    this.rates.forEach(r => {
      const b = ((r as any).brand || '').trim();
      if (b !== this.activeBrandTab) return;

      const period = r.period || '';
      if (!ratesByPeriod.has(period)) {
        ratesByPeriod.set(period, []);
      }
      ratesByPeriod.get(period)!.push(r);
    });

    const rateNamesSet = new Set<string>();
    const kcegTotalsByRateName: { [key: string]: number } = {};
    const kcegRatesByRateName: { [key: string]: number } = {};
    const lfcRatesByRateName: { [key: string]: number } = {};
    const lfcTotalsByRateName: { [key: string]: number } = {};
    const rateOrderMap = new Map<string, number>();

    this.cachedAggregations.forEach(agg => {
      const period = agg.period;
      if (this.selectedPeriod !== 'All Periods' && period !== this.selectedPeriod) return;

      let companyIds: string[] = [];
      if (agg.companyId) {
        companyIds = [agg.companyId];
      } else {
        const sid = String(agg.storeId || '').trim();
        companyIds = storeToCompanyMap.get(sid) || [];
      }

      if (companyIds.length === 0) return;

      const periodRates = ratesByPeriod.get(period) || [];

      companyIds.forEach(cid => {
        // Only process if this company/period is currently visible in the grid
        if (!visibleRowKeys.has(`${period}_${cid}`)) return;

        const currency = this.companyCurrencyMap.get(cid) || 'USD';
        const cached = this.exchangeRateCache.get(`${currency}_${period}`);
        const exchangeRate = cached ? cached.rate : 1;

        periodRates.forEach(rate => {
          const revenue = this.calculateRevenueForRate(agg, rate);
          const fee = revenue * (rate.rate / 100);

          if (fee !== 0) {
            const rateName = `${rate.name || 'Unknown'} Fee (${rate.rate}%)`;
            rateNamesSet.add(rateName);
            rateOrderMap.set(rateName, rate.order ?? 9999);

            const totalFee = fee * exchangeRate;

            const kcegRateVal = rate.kcegRate;
            const kcegRate = (kcegRateVal !== undefined && kcegRateVal !== null) ? Number(kcegRateVal) : 0;
            const lfcRate = (rate.rate || 0) - kcegRate;

            let kcegAmount = 0;
            let lfcAmount = 0;
            if (rate.rate && rate.rate !== 0) {
              kcegAmount = totalFee * (kcegRate / rate.rate);
              lfcAmount = totalFee * (lfcRate / rate.rate);
            }

            // KCEG fee should be all the additions for kceg fee from each company
            kcegTotalsByRateName[rateName] = (kcegTotalsByRateName[rateName] || 0) + kcegAmount;
            kcegRatesByRateName[rateName] = kcegRate;

            lfcTotalsByRateName[rateName] = (lfcTotalsByRateName[rateName] || 0) + lfcAmount;
            lfcRatesByRateName[rateName] = lfcRate;
          }
        });
      });
    });

    this.incentiveStats.rateNames = Array.from(rateNamesSet).sort((a, b) => {
      const orderA = rateOrderMap.get(a) ?? 9999;
      const orderB = rateOrderMap.get(b) ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });

    this.incentiveStats.rateNames.forEach(name => {
      const kceg = kcegTotalsByRateName[name] || 0;
      const lfc = lfcTotalsByRateName[name] || 0;
      const total = kceg + lfc;

      this.incentiveStats.total[name] = total;
      this.incentiveStats.lfc[name] = lfc;
      this.incentiveStats.kceg[name] = kceg;
      this.incentiveStats.kcegRates[name] = kcegRatesByRateName[name] || 0;
      this.incentiveStats.lfcRates[name] = lfcRatesByRateName[name] || 0;

      this.incentiveStats.grandTotal += total;
      this.incentiveStats.grandTotalLfc += lfc;
      this.incentiveStats.grandTotalKceg += kceg;
    });
  }

  async viewHistory(row: any) {
    if (!row.invoiceDocId) {
      this.invoiceHistory = [];
      this.isHistoryModalOpen = true;
      return;
    }

    this.isLoadingHistory = true;
    this.isHistoryModalOpen = true;
    try {
      this.invoiceHistory = await this.invoiceService.getInvoiceHistory(row.invoiceDocId);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      this.isLoadingHistory = false;
    }
  }

  closeHistoryModal() {
    this.isHistoryModalOpen = false;
    this.invoiceHistory = [];
  }

  private calculateRevenueForRate(d: any, rate: SmrsRate): number {
    let val = 0;
    rate.importKeys?.forEach(k => {
      // 1. Check Fee Type (revenues)
      if (d['revenues'] && d['revenues'][k]) {
        val += Number(d['revenues'][k]);
      }
      // 2. Check IP (ips)
      if (d['ips']) {
        Object.values(d['ips']).forEach((ipMap: any) => {
          if (ipMap && ipMap[k]) {
            val += Number(ipMap[k]);
          }
        });
      }
    });
    // Legacy/Direct ID match
    if (d['revenues'] && rate.id && d['revenues'][rate.id]) {
      val += Number(d['revenues'][rate.id]);
    }
    return val;
  }

  maxPaidPeriodMap: Map<string, string> = new Map();

  async recalculateLocks() {
    this.maxPaidPeriodMap.clear();
    const persistedUnlockedKeys = new Set<string>();
    try {
      const allInvoices = await this.invoiceService.getInvoices();
      allInvoices.forEach((inv: any) => {
        const cid = inv.companyId || inv.company?.id || (typeof inv.company === 'string' ? inv.company : '');
        const cName = (inv.company && typeof inv.company === 'object' ? inv.company.company : '') || inv.companyName || '';
        const period = inv.period;
        const status = inv.status;
        if (status === 'Paid' || status === 'Partial') {
          if (cid) {
            const currentMax = this.maxPaidPeriodMap.get(cid);
            if (!currentMax || period > currentMax) {
              this.maxPaidPeriodMap.set(cid, period);
            }
          }
          if (inv.companyId && inv.companyId !== cid) {
            const currentMax = this.maxPaidPeriodMap.get(inv.companyId);
            if (!currentMax || period > currentMax) {
              this.maxPaidPeriodMap.set(inv.companyId, period);
            }
          }
          if (cName) {
            const currentMax = this.maxPaidPeriodMap.get(cName);
            if (!currentMax || period > currentMax) {
              this.maxPaidPeriodMap.set(cName, period);
            }
          }
        }
        if (inv.isManuallyUnlocked === true || inv.isManuallyUnlocked === 'true') {
          if (period && cid) {
            persistedUnlockedKeys.add(`${period}_${cid}`);
          }
          if (period && inv.companyId) {
            persistedUnlockedKeys.add(`${period}_${inv.companyId}`);
          }
          if (period && cName) {
            persistedUnlockedKeys.add(`${period}_${cName}`);
          }
          if (inv.id) {
            persistedUnlockedKeys.add(inv.id);
          }
          if (inv.docId) {
            persistedUnlockedKeys.add(inv.docId);
          }
        }
      });
    } catch (e) {
      console.error('Failed to update maxPaidPeriodMap', e);
    }

    this.gridData.forEach(row => {
      const cid = row.company?.id || row.companyId || (typeof row.company === 'string' ? row.company : '');
      const cName = row.company?.company || row.companyName || '';
      const maxPaid = (cid ? this.maxPaidPeriodMap.get(cid) : null) || 
                      (row.companyId ? this.maxPaidPeriodMap.get(row.companyId) : null) ||
                      (cName ? this.maxPaidPeriodMap.get(cName) : null);
      const lockedByFuture = maxPaid ? maxPaid > row.period : false;
      const lockedByAppliedFrom = row.appliedFrom && row.appliedFrom.length > 0;
      const naturallyLocked = lockedByFuture || lockedByAppliedFrom;

      const isUnlockedGlobally = 
        (row.id && persistedUnlockedKeys.has(row.id)) ||
        (row.invoiceDocId && persistedUnlockedKeys.has(row.invoiceDocId)) ||
        (cid && row.period && persistedUnlockedKeys.has(`${row.period}_${cid}`)) ||
        (row.companyId && row.period && persistedUnlockedKeys.has(`${row.period}_${row.companyId}`)) ||
        (cName && row.period && persistedUnlockedKeys.has(`${row.period}_${cName}`)) ||
        row.isManuallyUnlocked === true ||
        row.isManuallyUnlocked === 'true' ||
        (row.id && this.unlockedInvoiceRowIds.has(row.id));

      row.isManuallyUnlocked = naturallyLocked && !!isUnlockedGlobally;
      row.isLocked = naturallyLocked && !isUnlockedGlobally;
    });

    this.cd.detectChanges();
  }
}   